import * as THREE from 'three';
import { UNIQUES, type NpcId, type UniqueId } from '@somnhold/content';
import { DSTEEL, DWOOD, LEATHER_BELT, STEEL, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, Ribbon, V, flame, glowSprite, heaterShield, limb, ring, sword, type Flame } from '../prims.ts';
import { figure, ghostify } from './heroes.ts';
import { pauldron } from './hero-tiers.ts';
import { LIGHT_K, begin, done, pointLight, type BaseOpts, type Model } from './types.ts';

/* ---------- the caravan (saga escort acts) ---------- */

/** The caravan's wagon: bed and sides banded in iron, two axles and four wheels (each a pivot the clips turn). */
const wagonBed = (c: ReturnType<typeof begin>): { wagon: THREE.Group; wheels: THREE.Group[]; plank: THREE.Material; board: THREE.Material } => {
  const { g } = c;
  const plank = std('#5b3d26', { roughness: 0.9 });
  const board = std('#4a3220', { roughness: 0.9 });
  const wagon = G(g, 0, 0, 0.18);
  wagon.name = 'wagon';
  // Bed and sides, banded in iron.
  M(new THREE.BoxGeometry(0.72, 0.08, 1.05), plank, wagon).position.y = 0.36;
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.04, 0.24, 1.05), board, wagon).position.set(k * 0.36, 0.5, 0);
    for (const z of [-0.4, 0.05, 0.45]) M(new THREE.BoxGeometry(0.05, 0.25, 0.03), DSTEEL, wagon).position.set(k * 0.37, 0.5, z);
  }
  for (const z of [-0.52, 0.52]) M(new THREE.BoxGeometry(0.72, 0.2, 0.04), board, wagon).position.set(0, 0.48, z);
  // Axles and four wheels (each a pivot, so the frames can turn them).
  const wheels: THREE.Group[] = [];
  for (const z of [-0.33, 0.36]) {
    limb(wagon, V(-0.42, 0.2, z), V(0.42, 0.2, z), 0.025, 0.025, DSTEEL, 5);
    for (const k of [-1, 1]) {
      const w = G(wagon, k * 0.42, 0.2, z);
      w.name = `wheel${z < 0 ? 'B' : 'F'}${k < 0 ? 'L' : 'R'}`;
      const rim = M(new THREE.TorusGeometry(0.19, 0.03, 5, 14), DWOOD, w);
      rim.rotation.y = Math.PI / 2;
      const tyre = M(new THREE.TorusGeometry(0.2, 0.012, 4, 14), DSTEEL, w);
      tyre.rotation.y = Math.PI / 2;
      const hub = M(new THREE.CylinderGeometry(0.045, 0.045, 0.07, 8), DSTEEL, w);
      hub.rotation.z = Math.PI / 2;
      for (let i = 0; i < 6; i++) {
        const sp = M(new THREE.BoxGeometry(0.02, 0.36, 0.025), WOOD, w);
        sp.rotation.x = (i / 6) * Math.PI;
      }
      wheels.push(w);
    }
  }
  return { wagon, wheels, plank, board };
};

/**
 * The caravan's crew, the same on every wagon: lantern poles at the front, the handle and the quarry golem
 * leaning into it, with the bones the clips move (anim/creatures/allies.ts). `extra` boils with the flames.
 */
const wagonCrew = (c: ReturnType<typeof begin>, wagon: THREE.Group, wheels: THREE.Group[], extra?: () => void): Model => {
  const { g, rng } = c;
  // Lantern poles at the front corners.
  const lanterns: { g: THREE.Group; f: Flame }[] = [];
  for (const k of [-1, 1]) {
    limb(wagon, V(k * 0.33, 0.4, 0.5), V(k * 0.33, 1.15, 0.5), 0.018, 0.015, DWOOD, 5);
    limb(wagon, V(k * 0.33, 1.13, 0.5), V(k * 0.33, 1.13, 0.64), 0.012, 0.012, DWOOD, 4);
    const lg = G(wagon, k * 0.33, 1.12, 0.64);
    lg.name = k < 0 ? 'lanternL' : 'lanternR';
    lg.rotation.x = -0.08;
    const lan = G(lg, 0, -0.1, 0);
    M(new THREE.CylinderGeometry(0.045, 0.05, 0.1, 6, 1, true), DSTEEL, lan);
    M(new THREE.ConeGeometry(0.06, 0.05, 6), DSTEEL, lan).position.y = 0.07;
    lanterns.push({ g: lg, f: flame(lan, V(0, -0.04, 0), 0.45, rng, '#ff9a3a') });
    glowSprite(lan, '#ffb877', 0.7, V(0, 0, 0), 0.55);
  }
  const L = pointLight(c, 0xffb070, 1.0, 3.5, V(0, 1.0, 0.85));
  // The handle at the back, and the golem pushing it.
  limb(wagon, V(-0.3, 0.5, -0.54), V(-0.3, 0.68, -0.72), 0.018, 0.018, DWOOD, 4);
  limb(wagon, V(0.3, 0.5, -0.54), V(0.3, 0.68, -0.72), 0.018, 0.018, DWOOD, 4);
  limb(wagon, V(-0.32, 0.68, -0.72), V(0.32, 0.68, -0.72), 0.022, 0.022, DWOOD, 5);
  const gst = stone('#7a7266'), rune = emis('#8fe9ff', '#2fbfe0', 0.9);
  const golem = G(g, 0, 0, -0.72);
  golem.name = 'golem';
  golem.rotation.x = 0.32;
  M(new THREE.BoxGeometry(0.38, 0.4, 0.28), gst, golem).position.y = 0.62;
  M(new THREE.BoxGeometry(0.2, 0.17, 0.2), gst, golem).position.set(0, 0.92, 0.04);
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.04, 0.025, 0.01), rune, golem).position.set(k * 0.05, 0.93, 0.145);
  M(new THREE.BoxGeometry(0.12, 0.03, 0.01), rune, golem).position.set(0, 0.64, 0.145);
  for (const k of [-1, 1]) {
    // Arms reach forward to the handle.
    limb(golem, V(k * 0.22, 0.76, 0.02), V(k * 0.28, 0.66, 0.26), 0.06, 0.055, gst, 6);
    M(new THREE.BoxGeometry(0.09, 0.08, 0.09), gst, golem).position.set(k * 0.29, 0.64, 0.3);
  }
  const legs: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const lg = G(golem, k * 0.11, 0.44, 0);
    lg.name = k < 0 ? 'legL' : 'legR';
    lg.rotation.x = k < 0 ? 0.45 : -0.35;
    limb(lg, V(0, 0, 0), V(0, -0.3, -0.05), 0.07, 0.075, gst, 6);
    M(new THREE.BoxGeometry(0.12, 0.08, 0.17), gst, lg).position.set(0, -0.34, 0);
    legs.push(lg);
  }
  // Only the flames and their light boil here; the bones move in the clips.
  const t = (): void => {
    for (const l of lanterns) l.f.flick();
    L.intensity = (0.8 + rng() * 0.4) * LIGHT_K;
    extra?.();
  };
  t();
  const bones: Record<string, THREE.Object3D> = { wagon };
  for (const w of wheels) bones[w.name] = w;
  for (const l of lanterns) bones[l.g.name] = l.g;
  bones.golem = golem;
  for (const l of legs) bones[l.name] = l;
  return done(c, 1.25, t, undefined, { bones: [g, ...Object.values(bones)], names: ['root', ...Object.keys(bones)] });
};

/**
 * The caravan of Musgoviejo: a stout wagon of ore, crates and sacks under a tarp, two lanterns on poles
 * at the front, pushed along by a quarry golem leaning into the handle. Faces +Z (where it rolls).
 * Rigged (anim/creatures/allies.ts): the wagon rocks, each wheel rolls, the lanterns swing and the golem strides;
 * the four baked frames only flicker the flames.
 */
export const cart = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 61);
  const { rng, R } = c;
  const { wagon, wheels, plank } = wagonBed(c);
  const sack = cloth('#56524a');
  const ore = stone('#4f4b47');
  const crystal = emis('#bfe8ff', '#3aa6ff', 0.9, { flatShading: true });
  // The load: ore with blue crystal in it, a lashed crate, sacks, a barrel, and a tarp over the back.
  for (let i = 0; i < 8; i++) {
    const r = M(new THREE.DodecahedronGeometry(0.09 + rng() * 0.05, 0), ore, wagon);
    r.position.set(R() * 0.5, 0.48 + rng() * 0.08, 0.15 + R() * 0.5);
    r.rotation.set(rng() * 3, rng() * 3, rng() * 3);
  }
  for (let i = 0; i < 3; i++) {
    const s = M(new THREE.OctahedronGeometry(0.035 + rng() * 0.02), crystal, wagon);
    s.position.set(R() * 0.4, 0.6, 0.2 + R() * 0.4);
    s.scale.y = 1.8;
  }
  glowSprite(wagon, '#5ab8ff', 0.4, V(0, 0.62, 0.25), 0.4);
  const crate = G(wagon, -0.15, 0.56, -0.25);
  M(new THREE.BoxGeometry(0.3, 0.26, 0.3), plank, crate);
  for (const y of [-0.08, 0.08]) M(new THREE.BoxGeometry(0.31, 0.03, 0.31), DSTEEL, crate).position.y = y;
  for (const [x, z] of [[0.17, -0.2], [0.2, -0.38]] as const) {
    const sk = M(new THREE.SphereGeometry(0.12, 8, 6), sack, wagon);
    sk.position.set(x, 0.55, z);
    sk.scale.set(1, 0.75, 1.2);
  }
  const barrel = M(new THREE.CylinderGeometry(0.1, 0.1, 0.24, 10), plank, wagon);
  barrel.position.set(0.18, 0.62, 0.38);
  ring(wagon, 0.102, 0.008, DSTEEL, 0.55, 4, 12).position.set(0.18, 0.55, 0.38);
  ring(wagon, 0.102, 0.008, DSTEEL, 0.69, 4, 12).position.set(0.18, 0.69, 0.38);
  const tp = M(new THREE.CylinderGeometry(0.3, 0.3, 0.5, 10, 1, true, -Math.PI / 2, Math.PI), std('#4e2224', { roughness: 1, side: THREE.DoubleSide }), wagon);
  tp.rotation.x = Math.PI / 2;
  tp.rotation.z = Math.PI / 2;
  tp.position.set(0, 0.58, -0.25);
  tp.scale.set(1, 1, 0.6);
  return wagonCrew(c, wagon, wheels);
};

/**
 * Brann's bet «El barril» (docs/SAGAS.md): the old garrison's beer, found in the cellars after forty years, a
 * fat oak cask lying in a cradle on the caravan's wagon, lashed down, with a brass tap at the front and the
 * garrison's tower burned into its head. The same wagon, lanterns and golem as `cart`, so it moves with the
 * same clips. Faces +Z. The leak is drawn by the view (amber drips that grow as it takes blows).
 */
export const barrelCart = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 67);
  const { rng } = c;
  const { wagon, wheels, board } = wagonBed(c);
  // Flat-shaded: each of the lathe's sixteen faces reads as a stave.
  const oak = std('#7a5432', { roughness: 0.8, flatShading: true });
  const hoop = metal('#3c3a38', { roughness: 0.6 });
  const head = std('#8a623c', { roughness: 0.85 });
  const burn = std('#1e140e', { roughness: 1 });
  const brass = metal('#c49a4a', { roughness: 0.35 });
  const wet = std('#3a2410', { roughness: 0.25 });
  const rope = std('#9a8460', { roughness: 1 });
  // The cradle: two chocks across the bed.
  for (const z of [-0.12, 0.4]) {
    const ck = M(new THREE.BoxGeometry(0.66, 0.1, 0.1), board, wagon);
    ck.position.set(0, 0.45, z);
  }
  // The cask, lying along the wagon: a bulging lathe of oak staves, four iron hoops.
  // Pushed forward so its head and tap stand proud of the front board, where the camera sees them.
  const cask = G(wagon, 0, 0.74, 0.16);
  cask.rotation.x = Math.PI / 2;
  const L = 0.42;
  const prof: THREE.Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const y = -L + (i / 10) * 2 * L;
    prof.push(new THREE.Vector2(0.24 + 0.065 * Math.cos((y / L) * (Math.PI / 2)), y));
  }
  M(new THREE.LatheGeometry(prof, 16), oak, cask);
  const radiusAt = (y: number): number => 0.24 + 0.065 * Math.cos((y / L) * (Math.PI / 2));
  for (const y of [-0.36, -0.2, 0.2, 0.36]) {
    const r = radiusAt(y) + 0.008;
    const hp = M(new THREE.TorusGeometry(r, 0.014, 4, 18), hoop, cask);
    hp.rotation.x = Math.PI / 2;
    hp.position.y = y;
  }
  // The heads, a little sunk; the front one dented, branded with the garrison's tower and tapped.
  for (const sgn of [-1, 1]) {
    const hd = M(new THREE.CylinderGeometry(0.235, 0.235, 0.02, 16), head, cask);
    hd.position.y = sgn * (L - 0.012);
  }
  const front = G(cask, 0, L + 0.002, 0);
  front.rotation.z = 0.04;
  // The brand: a ring with a tower inside (the Order's garrison), burned dark.
  const brandRing = M(new THREE.TorusGeometry(0.13, 0.012, 3, 20), burn, front);
  brandRing.rotation.x = Math.PI / 2;
  M(new THREE.BoxGeometry(0.06, 0.004, 0.13), burn, front).position.set(0, 0, 0.0);
  for (const x of [-0.04, 0, 0.04]) M(new THREE.BoxGeometry(0.022, 0.004, 0.03), burn, front).position.set(x, 0, -0.075);
  M(new THREE.BoxGeometry(0.03, 0.004, 0.035), burn, front).position.set(0, 0, 0.05);
  // The tap, low on the head, with a wet stain under it.
  const tap = G(front, 0, 0.02, 0.17);
  M(new THREE.CylinderGeometry(0.022, 0.026, 0.09, 8), brass, tap).position.y = 0.045;
  const spout = M(new THREE.CylinderGeometry(0.012, 0.014, 0.06, 6), brass, tap);
  spout.position.set(0, 0.085, 0.025);
  spout.rotation.x = Math.PI / 2;
  const key = M(new THREE.BoxGeometry(0.07, 0.014, 0.014), brass, tap);
  key.position.set(0, 0.1, -0.005);
  const stain = M(new THREE.CircleGeometry(0.07, 10), wet, front);
  stain.rotation.x = -Math.PI / 2;
  stain.position.set(0.01, 0.003, 0.2);
  stain.scale.set(0.8, 1.6, 1);
  // Lashed down: two ropes over the top, knotted to the sides.
  for (const z of [-0.22, 0.24]) {
    const lash = M(new THREE.TorusGeometry(radiusAt(z) + 0.012, 0.012, 4, 14, Math.PI), rope, wagon);
    lash.position.set(0, 0.74, z + 0.16);
    for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.026, 6, 4), rope, wagon).position.set(k * (radiusAt(z) + 0.012), 0.74, z + 0.16);
  }
  // A tin mug hung from the cradle (forty years waiting for it).
  const mug = G(wagon, 0.33, 0.5, 0.42);
  M(new THREE.CylinderGeometry(0.04, 0.036, 0.08, 8), metal('#8a8e94', { roughness: 0.5 }), mug);
  const handle = M(new THREE.TorusGeometry(0.025, 0.007, 4, 8, Math.PI), metal('#8a8e94', { roughness: 0.5 }), mug);
  handle.position.x = 0.04;
  handle.rotation.z = -Math.PI / 2;
  mug.rotation.z = 0.2 + rng() * 0.1;
  return wagonCrew(c, wagon, wheels);
};

/* ---------- the saga's people (portraits for the scenes) ---------- */

/** The saga's speaking parts that have a face (the narrator has none). */
export const NPC_IDS: readonly NpcId[] = ['brann', 'odna', 'hilde', 'gaspar', 'maelis', 'fenn', 'mada', 'ulla', 'cal', 'orla', 'dorn'];

/** Brann, the old sergeant: worn Order plate over leather, a faded blue tabard, a grey beard and a scar. */
const brann = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 71);
  const worn = metal('#7d838a', { roughness: 0.55, flatShading: true });
  const f = figure({ legs: leather('#3a3028'), boots: leather('#2a201a'), torso: worn, shoulders: worn, arms: leather('#4a3a2e'), forearms: DSTEEL, hands: leather('#3a2c22'), skin: std('#b48a72', { roughness: 0.8 }), cape: '#25324a' });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The tabard of the Order, faded and frayed at the hem.
  const tab = M(new THREE.BoxGeometry(0.24, 0.5, 0.015), cloth('#2c3a58'), b);
  tab.position.set(0, 1.08, 0.17);
  for (let i = 0; i < 5; i++) M(new THREE.BoxGeometry(0.04, 0.03 + (i % 2) * 0.03, 0.015), cloth('#2c3a58'), b).position.set(-0.1 + i * 0.05, 0.82, 0.17);
  M(new THREE.TorusGeometry(0.045, 0.01, 4, 10), metal('#9aa0a8'), b).position.set(0, 1.2, 0.18);
  pauldron(b, -1, worn, 1.05, 1, DSTEEL);
  // Dents and a sword at the hip; a flask on the belt.
  const sw = sword(0.62, STEEL, DSTEEL);
  b.add(sw);
  sw.position.set(0.2, 0.95, -0.02);
  sw.rotation.set(Math.PI - 0.2, 0, 0.15);
  const flask = M(new THREE.SphereGeometry(0.05, 7, 5), leather('#5a3a24'), b);
  flask.position.set(-0.17, 0.92, 0.1);
  flask.scale.y = 1.4;
  // Head: bald crown, a fringe of grey, a heavy beard and moustache; the scar across the left eye.
  const grey = std('#9c9a96', { roughness: 1 });
  const fringe = M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, Math.PI * 0.42, Math.PI * 0.2), grey, h);
  fringe.position.set(0, 1.64, -0.01);
  const beard = M(new THREE.ConeGeometry(0.1, 0.2, 8), grey, h);
  beard.position.set(0, 1.5, 0.08);
  beard.rotation.x = Math.PI + 0.25;
  const mst = M(new THREE.BoxGeometry(0.13, 0.03, 0.04), grey, h);
  mst.position.set(0, 1.58, 0.11);
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.05, 0.015, 0.02), std('#6e6a64'), h).position.set(k * 0.045, 1.68, 0.11);
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
  }
  const scar = M(new THREE.BoxGeometry(0.012, 0.11, 0.012), std('#8a4440', { roughness: 0.7 }), h);
  scar.position.set(0.055, 1.625, 0.112);
  scar.rotation.z = 0.5;
  f.rig.head.rotation.x = -0.05;
  return done(c, 1.8, () => undefined, f.rig);
};

/** Odna, the mayor of Musgoviejo: hard and tired, plain wool, a heavy shawl, keys at the belt. */
const odna = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 72);
  const wool = cloth('#3e4a40');
  const f = figure({ legs: wool, boots: leather('#2a201a'), torso: wool, arms: wool, hands: std('#c09478', { roughness: 0.8 }), skin: std('#c09478', { roughness: 0.8 }) });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // A long skirt over the legs.
  const skirt = M(new THREE.CylinderGeometry(0.19, 0.3, 0.86, 12, 1, true), std('#353e36', { roughness: 1, side: THREE.DoubleSide }), b);
  skirt.position.y = 0.47;
  // The shawl: a heavy drape round the shoulders, crossed in front.
  const shawlM = std('#5a2a26', { roughness: 1, side: THREE.DoubleSide, bumpScale: 0.03 });
  const sh = M(new THREE.CylinderGeometry(0.17, 0.3, 0.32, 12, 1, true), shawlM, b);
  sh.position.y = 1.33;
  for (const k of [-1, 1]) {
    const flap = M(new THREE.BoxGeometry(0.12, 0.3, 0.02), shawlM, b);
    flap.position.set(k * 0.06, 1.18, 0.19);
    flap.rotation.z = k * 0.4;
  }
  M(new THREE.ConeGeometry(0.22, 0.4, 3, 1, true), shawlM, b).position.set(0, 1.12, -0.2);
  // Keys of the village.
  const keys = G(b, 0.15, 0.93, 0.12);
  M(new THREE.TorusGeometry(0.03, 0.007, 4, 10), DSTEEL, keys);
  for (let i = 0; i < 3; i++) limb(keys, V(-0.01 + i * 0.012, -0.02, 0), V(-0.015 + i * 0.015, -0.09, 0.01), 0.005, 0.005, DSTEEL, 3);
  // Dark hair going grey, pulled back into a knot; a tired, set face.
  const hair = std('#4c4640', { roughness: 1 });
  const cap = M(new THREE.SphereGeometry(0.126, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  cap.position.set(0, 1.645, -0.01);
  M(new THREE.SphereGeometry(0.06, 8, 6), hair, h).position.set(0, 1.66, -0.13);
  M(new THREE.BoxGeometry(0.03, 0.025, 0.02), std('#7e7872'), h).position.set(-0.07, 1.72, 0.08);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
    // Heavy lids, a line under the eyes.
    M(new THREE.BoxGeometry(0.045, 0.012, 0.02), std('#9a7460'), h).position.set(k * 0.045, 1.672, 0.11);
    M(new THREE.BoxGeometry(0.035, 0.006, 0.01), std('#8a6656'), h).position.set(k * 0.045, 1.635, 0.115);
  }
  M(new THREE.BoxGeometry(0.05, 0.008, 0.01), std('#7a4a44'), h).position.set(0, 1.585, 0.115);
  f.rig.head.rotation.x = 0.08;
  f.rig.armL.rotation.set(-0.5, 0, 0.35);
  f.rig.armR.rotation.set(-0.5, 0, -0.35);
  return done(c, 1.78, () => undefined, f.rig);
};

/**
 * Fenn, the peddler of the titan: wiry and weathered, a wide-brimmed hat and a long scarf, and on his back a tall
 * pack frame hung with pots, a coil of rope, ribbons and a small lantern. Sells everything but advice.
 */
const fenn = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 74);
  const coat = cloth('#4b4236');
  const skin = std('#b98a6c', { roughness: 0.8 });
  const f = figure({ legs: cloth('#3a3530'), boots: leather('#2a201a'), torso: coat, arms: coat, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // A long travelling coat, open, its tails to the knee.
  const coatM = std('#4b4236', { roughness: 1, side: THREE.DoubleSide });
  const tail = M(new THREE.CylinderGeometry(0.2, 0.27, 0.5, 10, 1, true, Math.PI * 0.15, Math.PI * 1.7), coatM, b);
  tail.position.y = 0.72;
  // The scarf: a faded red, wound twice and one end hanging.
  const scarf = std('#7a3a30', { roughness: 1 });
  const wound = M(new THREE.TorusGeometry(0.11, 0.035, 6, 12), scarf, b);
  wound.position.set(0, 1.46, 0);
  wound.rotation.x = Math.PI / 2;
  const end = M(new THREE.BoxGeometry(0.07, 0.34, 0.025), scarf, b);
  end.position.set(0.08, 1.27, 0.15);
  end.rotation.z = -0.15;
  for (const y of [1.14, 1.16]) M(new THREE.BoxGeometry(0.075, 0.008, 0.028), std('#c9b8a0'), b).position.set(0.105 - (1.16 - y), y, 0.15);
  // The pack frame: two poles, crossbars and a lashed bundle; pots, rope, ribbons and a small lantern hung on it.
  const frame = G(b, 0, 0.95, -0.2);
  const pole = std('#5b4330', { roughness: 0.9 });
  for (const k of [-1, 1]) limb(frame, V(k * 0.17, 0, 0), V(k * 0.15, 0.95, -0.04), 0.018, 0.016, pole, 5);
  for (const y of [0.1, 0.48, 0.86]) limb(frame, V(-0.17, y, -0.01), V(0.17, y, -0.01), 0.013, 0.013, pole, 4);
  const bundle = M(new THREE.BoxGeometry(0.3, 0.38, 0.2), cloth('#5d5a4c'), frame);
  bundle.position.set(0, 0.32, -0.1);
  for (const y of [0.22, 0.42]) M(new THREE.BoxGeometry(0.32, 0.02, 0.22), leather('#3a2a1e'), frame).position.set(0, y, -0.1);
  const rope = M(new THREE.TorusGeometry(0.09, 0.022, 6, 12), std('#9a8566', { roughness: 1 }), frame);
  rope.position.set(0, 0.68, -0.12);
  const pot = metal('#3a3d44', { roughness: 0.5, metalness: 0.6 });
  for (const [x, y, r] of [[-0.2, 0.55, 0.06], [0.21, 0.42, 0.05], [0.19, 0.7, 0.045]] as const) {
    const p = M(new THREE.CylinderGeometry(r, r * 0.85, r * 1.2, 8), pot, frame);
    p.position.set(x, y, -0.05);
  }
  for (const [x, col] of [[-0.12, '#b03a2e'], [0.02, '#3a6a8a'], [0.12, '#d8d4c8']] as const) {
    const rb = M(new THREE.BoxGeometry(0.025, 0.22, 0.005), std(col, { roughness: 1, side: THREE.DoubleSide }), frame);
    rb.position.set(x, 0.78, 0.0);
    rb.rotation.z = (x * 3) % 0.4;
  }
  const lan = G(frame, -0.21, 0.88, 0.02);
  M(new THREE.CylinderGeometry(0.04, 0.05, 0.1, 6, 1, true), DSTEEL, lan);
  M(new THREE.ConeGeometry(0.055, 0.05, 6), DSTEEL, lan).position.y = 0.075;
  const fl = flame(lan, V(0, -0.02, 0), 0.4, c.rng, '#ffb46a');
  glowSprite(lan, '#ffb46a', 0.7, V(0, 0, 0), 0.45);
  // A lean, weathered face: stubble, a crooked grin, crow's feet.
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
    M(new THREE.BoxGeometry(0.04, 0.008, 0.02), std('#8a6248'), h).position.set(k * 0.045, 1.672, 0.11);
  }
  M(new THREE.SphereGeometry(0.1, 10, 6, 0, Math.PI * 2, Math.PI * 0.55, Math.PI * 0.3), std('#5a4a3e', { roughness: 1 }), h).position.set(0, 1.645, 0.0);
  const grin = M(new THREE.BoxGeometry(0.05, 0.008, 0.01), std('#6a3a34'), h);
  grin.position.set(0.008, 1.59, 0.115);
  grin.rotation.z = 0.15;
  // The wide-brimmed hat, its band holding a feather.
  const felt = std('#2e2a26', { roughness: 1 });
  const brim = M(new THREE.CylinderGeometry(0.24, 0.25, 0.02, 14), felt, h);
  brim.position.y = 1.735;
  brim.rotation.x = 0.08;
  M(new THREE.CylinderGeometry(0.105, 0.13, 0.13, 12), felt, h).position.y = 1.8;
  M(new THREE.CylinderGeometry(0.132, 0.132, 0.025, 12), std('#7a3a30'), h).position.y = 1.755;
  const feather = M(new THREE.BoxGeometry(0.02, 0.2, 0.005), std('#d8d4c8', { side: THREE.DoubleSide }), h);
  feather.position.set(0.12, 1.84, -0.03);
  feather.rotation.z = -0.5;
  f.rig.head.rotation.x = 0.05;
  f.rig.armL.rotation.set(-0.3, 0, 0.3);
  // One hand out, palm up, as if offering a deal.
  f.rig.armR.rotation.set(-1.0, 0, -0.35);
  return done(c, 1.95, () => fl.flick(), f.rig);
};

/** Hilde, the dwarf smith of the Hondura: broad and short, a scorched leather apron, braids and a hammer. */
const hilde = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 73);
  const shirt = cloth('#4a2f2a');
  const skin = std('#c49274', { roughness: 0.75 });
  const f = figure({ legs: leather('#3a2a20'), boots: leather('#241a14'), torso: shirt, arms: shirt, forearms: skin, hands: leather('#2e221a'), skin });
  f.fig.scale.set(1.18, 0.8, 1.18);
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The apron, scorched at the hem, with a pocket of tongs.
  const apron = M(new THREE.BoxGeometry(0.3, 0.72, 0.02), leather('#2c211a'), b);
  apron.position.set(0, 0.98, 0.18);
  M(new THREE.BoxGeometry(0.3, 0.06, 0.022), std('#1a1412', { roughness: 1 }), b).position.set(0, 0.65, 0.181);
  M(new THREE.BoxGeometry(0.14, 0.1, 0.03), leather('#3a2a20'), b).position.set(0.05, 0.92, 0.2);
  limb(b, V(0.03, 0.95, 0.21), V(0.05, 1.07, 0.22), 0.006, 0.006, DSTEEL, 3);
  ring(b, 0.17, 0.02, LEATHER_BELT, 1.0, 4, 14);
  for (const k of [-1, 1]) M(new THREE.CylinderGeometry(0.058, 0.058, 0.08, 8), DSTEEL, b).position.set(k * 0.28, 0.98, 0.1);
  // Copper hair in two thick braids and a knot; soot on the cheek.
  const hair = std('#8a3a1e', { roughness: 0.95 });
  const cap = M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  cap.position.set(0, 1.645, -0.01);
  for (const k of [-1, 1]) {
    const pts = [V(k * 0.11, 1.62, 0.02), V(k * 0.13, 1.5, 0.05), V(k * 0.12, 1.38, 0.08), V(k * 0.1, 1.28, 0.1)];
    for (let i = 0; i < 3; i++) limb(h, pts[i]!, pts[i + 1]!, 0.032 - i * 0.004, 0.028 - i * 0.004, hair, 6);
    M(new THREE.TorusGeometry(0.022, 0.007, 4, 8), DSTEEL, h).position.copy(pts[3]!);
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
    M(new THREE.BoxGeometry(0.05, 0.016, 0.02), hair, h).position.set(k * 0.045, 1.685, 0.108);
  }
  M(new THREE.SphereGeometry(0.03, 6, 4), std('#3a3230', { roughness: 1 }), h).position.set(0.07, 1.6, 0.1);
  // The hammer.
  const ham = G(f.rhAbs, 0.28, 0.91, 0.13);
  ham.rotation.set(0.3, 0, 0.75);
  limb(ham, V(0, -0.05, 0), V(0, 0.42, 0), 0.016, 0.016, DWOOD, 5);
  M(new THREE.BoxGeometry(0.16, 0.08, 0.08), DSTEEL, ham).position.y = 0.42;
  M(new THREE.BoxGeometry(0.04, 0.09, 0.09), STEEL, ham).position.set(0.08, 0.42, 0);
  // The hammer held across the chest, its head by the left shoulder.
  f.rig.armR.rotation.set(-0.85, 0, 0.5);
  return done(c, 1.42, () => undefined, f.rig);
};

/**
 * Dorn, Hilde's cousin, forty years alone on the Anvil watch: an old dwarf, stooped and broad, a long beard gone grey
 * with streaks of black, soot ground into his face and arms, a leather apron worn through and patched, and over his
 * shoulder the great winding key he keeps the machines going with, as long as his arm.
 */
const dorn = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 79);
  const shirt = cloth('#3a3530');
  const skin = std('#b08a72', { roughness: 0.8 });
  const sooty = std('#6e5a4e', { roughness: 0.85 });
  const f = figure({ legs: leather('#2e261f'), boots: leather('#1e1814'), torso: shirt, arms: shirt, forearms: sooty, hands: leather('#2a201a'), skin });
  f.fig.scale.set(1.2, 0.78, 1.2);
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The apron: old leather worn pale at the knees, a patch stitched on, scorched along the hem.
  const apronM = leather('#3a2c22');
  const apron = M(new THREE.BoxGeometry(0.31, 0.74, 0.02), apronM, b);
  apron.position.set(0, 0.97, 0.18);
  M(new THREE.BoxGeometry(0.31, 0.07, 0.022), std('#17120f', { roughness: 1 }), b).position.set(0, 0.63, 0.181);
  M(new THREE.BoxGeometry(0.11, 0.1, 0.024), leather('#5a4636'), b).position.set(-0.07, 0.8, 0.182);
  for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.012, 0.012, 0.026), std('#9a8a74'), b).position.set(-0.115 + i * 0.03, 0.852, 0.184);
  ring(b, 0.17, 0.022, LEATHER_BELT, 1.0, 4, 14);
  // Tools on the belt: a small hammer, an oil can, a ring of little keys.
  limb(b, V(0.19, 1.0, 0.08), V(0.22, 0.82, 0.1), 0.012, 0.012, DWOOD, 4);
  M(new THREE.BoxGeometry(0.07, 0.035, 0.035), DSTEEL, b).position.set(0.22, 0.81, 0.1);
  const can = M(new THREE.CylinderGeometry(0.035, 0.045, 0.08, 8), metal('#4a4e56', { roughness: 0.5 }), b);
  can.position.set(-0.2, 0.92, 0.07);
  limb(b, V(-0.2, 0.96, 0.07), V(-0.24, 1.01, 0.12), 0.006, 0.004, DSTEEL, 3);
  const keys = G(b, -0.14, 0.94, 0.14);
  M(new THREE.TorusGeometry(0.028, 0.006, 4, 10), DSTEEL, keys);
  for (let i = 0; i < 3; i++) limb(keys, V(-0.012 + i * 0.012, -0.02, 0), V(-0.016 + i * 0.016, -0.075, 0.01), 0.004, 0.004, DSTEEL, 3);
  // Soot on the shoulders and the chest.
  const soot = std('#1c1816', { roughness: 1 });
  for (const [x, y, z, r] of [[0.12, 1.28, 0.16, 0.05], [-0.15, 1.33, 0.12, 0.04], [0.04, 1.15, 0.19, 0.035]] as const) {
    const sm = M(new THREE.SphereGeometry(r, 6, 4), soot, b);
    sm.position.set(x, y, z);
    sm.scale.set(1, 0.7, 0.25);
  }
  // Head: bald and spotted on top, a ring of grey hair, bushy brows, a nose broken long ago; the beard long, grey
  // streaked black, in two plaits bound with iron down to the belt.
  const grey = std('#8e8a84', { roughness: 1 });
  const black = std('#2a2624', { roughness: 1 });
  const fringe = M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, Math.PI * 0.4, Math.PI * 0.22), grey, h);
  fringe.position.set(0, 1.645, -0.01);
  M(new THREE.SphereGeometry(0.018, 5, 4), std('#8a6650'), h).position.set(0.04, 1.77, 0.03);
  const beard = M(new THREE.ConeGeometry(0.13, 0.26, 9), grey, h);
  beard.position.set(0, 1.47, 0.08);
  beard.rotation.x = Math.PI + 0.2;
  const streak = M(new THREE.ConeGeometry(0.05, 0.22, 6), black, h);
  streak.position.set(0.03, 1.47, 0.135);
  streak.rotation.x = Math.PI + 0.25;
  for (const k of [-1, 1]) {
    const pts = [V(k * 0.05, 1.38, 0.13), V(k * 0.06, 1.24, 0.16), V(k * 0.05, 1.1, 0.18), V(k * 0.04, 0.98, 0.2)];
    for (let i = 0; i < 3; i++) limb(h, pts[i]!, pts[i + 1]!, 0.034 - i * 0.006, 0.03 - i * 0.006, i === 1 && k > 0 ? black : grey, 6);
    M(new THREE.TorusGeometry(0.022, 0.008, 4, 8), DSTEEL, h).position.copy(pts[2]!);
    M(new THREE.BoxGeometry(0.055, 0.022, 0.03), grey, h).position.set(k * 0.045, 1.69, 0.108);
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
  }
  const mst = M(new THREE.BoxGeometry(0.15, 0.035, 0.045), grey, h);
  mst.position.set(0, 1.585, 0.11);
  const nose = M(new THREE.SphereGeometry(0.03, 6, 5), std('#a07462', { roughness: 0.7 }), h);
  nose.position.set(0.008, 1.625, 0.125);
  nose.scale.set(1, 1.2, 1);
  M(new THREE.SphereGeometry(0.03, 6, 4), soot, h).position.set(-0.075, 1.6, 0.09);
  // The winding key over his right shoulder: an iron shaft as long as his arm, a crossbar grip at one end, a squared
  // bit with teeth at the other.
  const key = G(f.rhAbs, 0.26, 0.92, 0.12);
  key.rotation.set(0.25, 0, 0.55);
  const iron = metal('#5c6068', { roughness: 0.45, flatShading: true });
  limb(key, V(0, -0.12, 0), V(0, 0.62, 0), 0.02, 0.02, iron, 6);
  const grip = M(new THREE.CylinderGeometry(0.024, 0.024, 0.3, 6), DWOOD, key);
  grip.position.y = -0.14;
  grip.rotation.z = Math.PI / 2;
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.03, 6, 4), iron, key).position.set(k * 0.15, -0.14, 0);
  const bit = G(key, 0, 0.64, 0);
  M(new THREE.BoxGeometry(0.07, 0.1, 0.07), DSTEEL, bit);
  for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.06 - i * 0.012, 0.02, 0.09), DSTEEL, bit).position.y = 0.07 + i * 0.03;
  M(new THREE.TorusGeometry(0.04, 0.008, 4, 10), metal('#8a8e96', { roughness: 0.4 }), key).position.y = 0.5;
  // Stooped from the years, the key on his shoulder; the other hand on his belt.
  f.rig.head.rotation.x = 0.12;
  f.rig.armR.rotation.set(-0.9, 0, 0.45);
  f.rig.armL.rotation.set(-0.2, 0, 0.35);
  return done(c, 1.4, () => undefined, f.rig);
};

/** Ser Gaspar Venn's spectre: a commander in fine plate and a long cape, dreamed on guard; translucent. */
const gaspar = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 74);
  const plate = metal('#c4cad2', { roughness: 0.25, flatShading: true });
  const f = figure({ legs: plate, boots: plate, torso: plate, shoulders: plate, arms: plate, forearms: plate, hands: plate, cape: '#1f3157', capeLen: 1.3, capeR: 0.5, capeArc: 2.2 });
  const b = f.bodyAbs, h = f.headAbs;
  for (const k of [-1, 1]) pauldron(b, k, plate, 1.3, 2, DSTEEL);
  ring(b, 0.13, 0.035, plate, 1.47, 5, 14);
  // The commander's helm, open-faced, with a high crest; a gaunt face under it.
  const helm = M(new THREE.SphereGeometry(0.14, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.6), plate, h);
  helm.position.y = 1.66;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.12, 0.1), plate, h).position.set(k * 0.12, 1.6, 0.04);
  M(new THREE.BoxGeometry(0.035, 0.12, 0.32), cloth('#2a3e6a'), h).position.set(0, 1.84, -0.03);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.015, 5, 4), std('#0c0a0c'), h).position.set(k * 0.045, 1.65, 0.115);
  // The longsword planted before him, hands on the pommel.
  const sw = sword(0.95, plate, DSTEEL);
  b.add(sw);
  sw.position.set(0, -0.12, 0.3);
  f.rig.armL.rotation.set(-0.55, 0, 0.45);
  f.rig.armR.rotation.set(-0.55, 0, -0.45);
  const sh = heaterShield(0.36, 0.5, metal('#1f3a6e', { roughness: 0.45 }));
  b.add(sh);
  sh.position.set(0, 1.2, -0.24);
  sh.rotation.y = Math.PI;
  c.g.add(f.fig);
  const tick = ghostify(c, f.fig, '#7fe3d6', () => undefined);
  return done(c, 1.95, tick, f.rig);
};

/**
 * Maelis, the village healer the ash took: still a woman — a hooded robe gone grey with ash, a satchel
 * of herbs at the hip — but her skin is cracked with embers, her eyes burn green, ash falls from her
 * hem, and she holds up the green-fire lantern the children see in their dreams.
 */
const maelis = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 75);
  const ash = std('#5c5a58', { roughness: 1, bumpScale: 0.04 });
  const robeM = std('#4a4846', { roughness: 1, side: THREE.DoubleSide, bumpScale: 0.04 });
  const skin = std('#8a7e78', { roughness: 0.9, flatShading: true });
  const ember = emis('#ff8a3a', '#ff5a1a', 1.2);
  const green = emis('#9dff8a', '#3aff4a', 1.6);
  const f = figure({ legs: ash, boots: std('#2a2624'), torso: ash, arms: ash, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The long robe, ragged and burnt at the hem, ash flaking off it.
  const robe = M(new THREE.CylinderGeometry(0.18, 0.34, 1.0, 12, 1, true), robeM, b);
  robe.position.y = 0.52;
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const tat = M(new THREE.ConeGeometry(0.05, 0.14, 3), robeM, b);
    tat.position.set(Math.cos(a) * 0.32, 0.02, Math.sin(a) * 0.32);
    tat.rotation.x = Math.PI;
  }
  for (let i = 0; i < 6; i++) M(new THREE.BoxGeometry(0.03, 0.02, 0.02), ember, b).position.set(-0.25 + i * 0.1, 0.05 + (i % 2) * 0.03, 0.27 - Math.abs(i - 2.5) * 0.02);
  // The healer's satchel of herbs, sprigs sticking out.
  const bag = M(new THREE.BoxGeometry(0.14, 0.12, 0.07), leather('#4a3a2a'), b);
  bag.position.set(-0.2, 0.92, 0.1);
  limb(b, V(-0.12, 1.4, 0.12), V(-0.2, 0.98, 0.11), 0.008, 0.008, LEATHER_BELT, 3);
  for (let i = 0; i < 3; i++) limb(b, V(-0.23 + i * 0.03, 0.97, 0.1), V(-0.25 + i * 0.04, 1.06, 0.12), 0.006, 0.004, std('#4a6a34'), 3);
  // Hood up; a gaunt face with ember cracks down the cheek and green eyes.
  // Open at the front (+z) so the face shows.
  const hood = M(new THREE.SphereGeometry(0.16, 12, 10, Math.PI / 2 + 0.75, Math.PI * 2 - 1.5, 0, Math.PI * 0.62), robeM, h);
  hood.position.set(0, 1.66, -0.02);
  hood.scale.set(1, 1.1, 1.08);
  M(new THREE.ConeGeometry(0.17, 0.26, 10, 1, true), robeM, h).position.set(0, 1.43, -0.05);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.015, 6, 4), green, h).position.set(k * 0.045, 1.655, 0.115);
    glowSprite(h, '#58ff6a', 0.6, V(k * 0.045, 1.655, 0.13), 0.08);
  }
  for (const [x, y, r] of [[0.06, 1.61, 0.4], [0.075, 1.58, -0.3], [-0.05, 1.6, -0.5]] as const) {
    const cr = M(new THREE.BoxGeometry(0.008, 0.06, 0.008), ember, h);
    cr.position.set(x, y, 0.113);
    cr.rotation.z = r;
  }
  // Cracks on the hands too.
  M(new THREE.BoxGeometry(0.03, 0.006, 0.006), ember, f.rhAbs).position.set(0.27, 0.9, 0.12);
  // The lantern of green fire, held out at her side.
  f.rig.armR.rotation.set(-0.95, 0, -0.45);
  const lan = G(c.g, 0.36, 1.12, 0.36);
  limb(lan, V(0, 0.16, 0), V(0, 0.06, 0), 0.008, 0.008, DSTEEL, 3);
  M(new THREE.CylinderGeometry(0.05, 0.06, 0.12, 6, 1, true), DSTEEL, lan);
  M(new THREE.ConeGeometry(0.07, 0.06, 6), DSTEEL, lan).position.y = 0.09;
  const fl = flame(lan, V(0, -0.03, 0), 0.5, c.rng, '#5aff6a');
  glowSprite(lan, '#58ff6a', 0.9, V(0, 0, 0), 0.6);
  const L = pointLight(c, 0x58ff6a, 1.1, 3, V(0.36, 1.1, 0.45));
  f.rig.head.rotation.x = 0.06;
  return done(
    c,
    1.8,
    () => {
      fl.flick();
      L.intensity = (0.9 + c.rng() * 0.3) * LIGHT_K;
    },
    f.rig,
  );
};

/**
 * Mada, Cal's mother, who mends nets in Musgoviejo: an older woman, a grey braid over the shoulder, a dark shawl,
 * a net over one arm and the needle in the other hand; a tired, kind face.
 */
const mada = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 76);
  const dress = cloth('#3d4248');
  const skin = std('#c49a80', { roughness: 0.85 });
  const f = figure({ legs: dress, boots: leather('#2a221c'), torso: dress, arms: dress, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // A long skirt and an apron.
  M(new THREE.CylinderGeometry(0.19, 0.31, 0.86, 12, 1, true), std('#34393f', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.47;
  const apron = M(new THREE.BoxGeometry(0.26, 0.5, 0.02), cloth('#8a8e94'), b);
  apron.position.set(0, 0.72, 0.2);
  apron.rotation.x = -0.12;
  // The shawl, dark blue-grey, knotted at the chest.
  const shawlM = std('#2c3440', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.17, 0.31, 0.3, 12, 1, true), shawlM, b).position.y = 1.33;
  M(new THREE.SphereGeometry(0.04, 6, 5), shawlM, b).position.set(0, 1.24, 0.17);
  // Grey hair, a braid over the left shoulder.
  // Grey with the dark still in it, so it reads as hair against her face (a pale grey read as a bald head).
  const hair = std('#7c7872', { roughness: 1 });
  const cap = M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), hair, h);
  cap.position.set(0, 1.64, -0.015);
  M(new THREE.SphereGeometry(0.055, 8, 6), hair, h).position.set(0, 1.7, -0.11);
  for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.03 - i * 0.003, 6, 5), hair, b).position.set(-0.1 - i * 0.005, 1.5 - i * 0.07, 0.11 + i * 0.01);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
    // Soft lids and the lines of a long life.
    M(new THREE.BoxGeometry(0.045, 0.012, 0.02), std('#a07a66'), h).position.set(k * 0.045, 1.674, 0.11);
    M(new THREE.BoxGeometry(0.03, 0.005, 0.01), std('#8e6a58'), h).position.set(k * 0.05, 1.63, 0.116);
  }
  M(new THREE.BoxGeometry(0.045, 0.008, 0.01), std('#86584e'), h).position.set(0, 1.588, 0.115);
  // A fishing net over the left arm, its floats of cork; the needle in the right hand.
  const net = G(b, -0.24, 0.98, 0.12);
  const netM = std('#7a7466', { roughness: 1, side: THREE.DoubleSide, transparent: true, opacity: 0.85 });
  const drape = M(new THREE.PlaneGeometry(0.22, 0.42, 3, 5), netM, net);
  drape.rotation.set(0, 0.6, 0.15);
  for (let i = 0; i < 4; i++) {
    M(new THREE.BoxGeometry(0.004, 0.42, 0.004), std('#5e584c'), net).position.set(-0.08 + i * 0.055, 0, 0.004);
    M(new THREE.BoxGeometry(0.22, 0.004, 0.004), std('#5e584c'), net).position.set(0, -0.18 + i * 0.12, 0.004);
  }
  for (const y of [-0.2, 0.2]) M(new THREE.CylinderGeometry(0.02, 0.02, 0.04, 6), std('#a0784a'), net).position.set(0.11, y, 0.01);
  const needle = M(new THREE.BoxGeometry(0.012, 0.11, 0.006), std('#c9bba0'), b);
  needle.position.set(0.22, 0.98, 0.18);
  needle.rotation.z = 0.5;
  f.rig.head.rotation.x = 0.16;
  f.rig.armL.rotation.set(-0.7, 0, 0.3);
  f.rig.armR.rotation.set(-0.8, 0, -0.35);
  return done(c, 1.76, () => undefined, f.rig);
};

/**
 * Ulla, chief of the clan of the Horn, up on the titan's tail: a weathered herder woman, a heavy fur mantle over
 * hide and wool, two long braids, a hunting horn at her belt and a herder's spear. Serious; she asks for nothing.
 */
const ulla = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 77);
  const hide = leather('#5a4636');
  const wool = cloth('#4a5048');
  const skin = std('#b0805e', { roughness: 0.85 });
  const f = figure({ legs: leather('#3e3228'), boots: leather('#2a201a'), torso: wool, shoulders: hide, arms: wool, forearms: hide, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // A hide skirt to the knee over the leggings, laced at the side.
  M(new THREE.CylinderGeometry(0.19, 0.27, 0.42, 10, 1, true), std('#4e3c2e', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.72;
  // The fur mantle: a thick grey-brown ring over the shoulders, falling behind to the calves.
  const fur = std('#6e6458', { roughness: 1, flatShading: true });
  const furDark = std('#4e463e', { roughness: 1, flatShading: true });
  const collar = M(new THREE.TorusGeometry(0.2, 0.075, 6, 14), fur, b);
  collar.position.set(0, 1.44, -0.01);
  collar.rotation.x = Math.PI / 2;
  collar.scale.set(1, 1, 0.8);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    M(new THREE.IcosahedronGeometry(0.05, 0), i % 2 ? fur : furDark, b).position.set(Math.cos(a) * 0.2, 1.46 + (i % 3) * 0.012, Math.sin(a) * 0.17);
  }
  const back = M(new THREE.CylinderGeometry(0.24, 0.32, 1.05, 10, 1, true, Math.PI * 0.55, Math.PI * 0.9), std('#5e554a', { roughness: 1, side: THREE.DoubleSide }), b);
  back.position.set(0, 0.94, -0.02);
  // A belt of plates, and at it the clan's hunting horn: a curved ox horn bound in leather, its mouth banded in iron.
  ring(b, 0.185, 0.03, leather('#2e241c'), 0.98, 5, 14);
  const horn = G(b, -0.19, 0.95, 0.08);
  const hornM = std('#d6cbb6', { roughness: 0.6 });
  for (let i = 0; i < 6; i++) {
    const k = i / 5;
    const seg = M(new THREE.CylinderGeometry(0.022 + k * 0.022, 0.018 + k * 0.022, 0.06, 7), i === 5 ? DSTEEL : i % 2 ? hornM : std('#bfb29a', { roughness: 0.6 }), horn);
    seg.position.set(-Math.sin(k * 1.4) * 0.09, -0.02 + k * 0.1 - k * k * 0.04, Math.cos(k * 1.4) * 0.05 - 0.05);
    seg.rotation.set(0.3, 0, 1.0 - k * 1.2);
  }
  limb(horn, V(0, 0.03, -0.04), V(0.04, 0.09, 0), 0.006, 0.006, leather('#2e241c'), 3);
  // The herder's spear, taller than she is, held upright.
  // (Held low, its head at her brow: the portrait frames her, not the spear.)
  const spear = G(b, 0.29, 0.45, 0.12);
  limb(spear, V(0, 0, 0), V(0, 1.3, 0), 0.018, 0.016, std('#5b4330', { roughness: 0.9 }), 5);
  M(new THREE.ConeGeometry(0.04, 0.18, 4), STEEL, spear).position.y = 1.39;
  for (const y of [1.24, 1.28]) M(new THREE.TorusGeometry(0.02, 0.006, 4, 8), leather('#2e241c'), spear).position.y = y;
  // A tuft of red wool under the head: the clan's mark.
  M(new THREE.ConeGeometry(0.03, 0.09, 5), cloth('#a43a2c'), spear).position.y = 1.22;
  // Dark hair in two long braids over the chest; a hard, wind-burnt face; a bone bead at the brow.
  const hair = std('#2e2620', { roughness: 1 });
  const cap = M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  cap.position.set(0, 1.645, -0.01);
  for (const k of [-1, 1]) {
    for (let i = 0; i < 7; i++) M(new THREE.SphereGeometry(0.03 - i * 0.002, 6, 5), hair, b).position.set(k * (0.1 + i * 0.004), 1.52 - i * 0.065, 0.1 + i * 0.012);
    M(new THREE.CylinderGeometry(0.02, 0.02, 0.025, 6), cloth('#a43a2c'), b).position.set(k * 0.128, 1.06, 0.18);
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.045, 1.655, 0.115);
    // Low, hard brows; lines from the wind.
    M(new THREE.BoxGeometry(0.05, 0.014, 0.02), std('#2e2620'), h).position.set(k * 0.045, 1.68, 0.112);
    M(new THREE.BoxGeometry(0.004, 0.035, 0.008), std('#8a5e44'), h).position.set(k * 0.075, 1.61, 0.105);
  }
  M(new THREE.BoxGeometry(0.05, 0.008, 0.01), std('#6e3e30'), h).position.set(0, 1.588, 0.115);
  M(new THREE.SphereGeometry(0.014, 6, 4), std('#e8e0d0'), h).position.set(0, 1.735, 0.1);
  f.rig.head.rotation.x = -0.03;
  f.rig.armL.rotation.set(-0.15, 0, 0.18);
  f.rig.armR.rotation.set(-0.35, 0, -0.18);
  return done(c, 1.85, () => undefined, f.rig);
};

/**
 * Cal, the young shepherd from Musgoviejo working the season with the clan: plain undyed wool, a sheepskin vest, a
 * shepherd's crook over the shoulder and a bundle hung from it; tousled hair, a boy's easy face.
 */
const cal = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 78);
  const wool = cloth('#8a8478');
  const skin = std('#c99a7a', { roughness: 0.8 });
  const f = figure({ legs: cloth('#4a463e'), boots: leather('#3a2c20'), torso: wool, arms: wool, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The sheepskin vest: pale fleece at the edges.
  const fleece = std('#d8d2c4', { roughness: 1, flatShading: true });
  const vest = M(new THREE.CylinderGeometry(0.225, 0.19, 0.46, 10, 1, true, Math.PI * 0.62, Math.PI * 1.76), std('#7a5a3e', { roughness: 1, side: THREE.DoubleSide }), b);
  vest.position.y = 1.2;
  for (let i = 0; i < 6; i++) for (const k of [-1, 1]) M(new THREE.IcosahedronGeometry(0.028, 0), fleece, b).position.set(k * 0.075, 1.42 - i * 0.075, 0.19);
  M(new THREE.TorusGeometry(0.19, 0.035, 5, 12), fleece, b).position.set(0, 1.44, 0);
  // A rope belt, a little water skin.
  ring(b, 0.175, 0.018, std('#9a8566', { roughness: 1 }), 0.98, 4, 12);
  const skinBag = M(new THREE.SphereGeometry(0.055, 7, 5), leather('#6a4a30'), b);
  skinBag.position.set(0.17, 0.9, 0.08);
  skinBag.scale.y = 1.3;
  // The shepherd's crook, upright at his right side: a long staff and its open hook curling over at the top.
  const crook = G(b, 0.3, 0, 0.1);
  const pole = std('#6b4f34', { roughness: 0.9 });
  limb(crook, V(0, 0.05, 0), V(0, 1.62, 0), 0.017, 0.015, pole, 5);
  // The hook: up, over and down, a short drop at the end, like the crook of a walking stick.
  const hook = M(new THREE.TorusGeometry(0.075, 0.015, 5, 12, Math.PI), pole, crook);
  hook.position.set(-0.075, 1.62, 0);
  limb(crook, V(-0.15, 1.62, 0), V(-0.145, 1.54, 0), 0.015, 0.013, pole, 4);
  // A young man's face: brown hair under a plain wool cap, a fringe over the brow, eyes, brows, a nose, a grin.
  const hair = std('#6a4a30', { roughness: 1 });
  M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), hair, h).position.set(0, 1.65, -0.012);
  // The fringe, flat against the forehead, and hair over the ears.
  const fringe = M(new THREE.BoxGeometry(0.17, 0.04, 0.03), hair, h);
  fringe.position.set(0, 1.715, 0.1);
  fringe.rotation.x = -0.35;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.08, 0.08), hair, h).position.set(k * 0.122, 1.66, 0.01);
  // The wool cap: a low dome of undyed grey-blue wool with a rolled brim.
  const wool2 = cloth('#5a6a7a');
  M(new THREE.SphereGeometry(0.138, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), wool2, h).position.set(0, 1.69, -0.02);
  const brim = M(new THREE.TorusGeometry(0.128, 0.022, 5, 16), wool2, h);
  brim.position.set(0, 1.74, -0.015);
  brim.rotation.x = Math.PI / 2 + 0.12;
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.014, 6, 4), std('#141214'), h).position.set(k * 0.042, 1.66, 0.117);
    M(new THREE.BoxGeometry(0.04, 0.01, 0.015), hair, h).position.set(k * 0.044, 1.687, 0.114);
    M(new THREE.SphereGeometry(0.018, 5, 4), std('#d98c78', { roughness: 0.9 }), h).position.set(k * 0.068, 1.615, 0.1);
  }
  const nose = M(new THREE.ConeGeometry(0.016, 0.04, 5), std('#c08a6a', { roughness: 0.8 }), h);
  nose.position.set(0, 1.63, 0.13);
  nose.rotation.x = Math.PI / 2;
  const grin = M(new THREE.BoxGeometry(0.05, 0.01, 0.01), std('#7a3a34'), h);
  grin.position.set(0, 1.59, 0.116);
  f.rig.head.rotation.x = -0.02;
  f.rig.head.rotation.y = 0.08;
  // The right hand on the staff at chest height, the left loose.
  f.rig.armR.rotation.set(-0.55, 0, -0.12);
  f.rig.armL.rotation.set(-0.15, 0, 0.18);
  return done(c, 1.8, () => undefined, f.rig);
};

/**
 * Orla, the old wayfarer and mapmaker of the Burn: a long travel cloak worn pale at the hem, boots grey with ash to
 * the knee, a map case slung across her back and a rolled map under her left arm, a knotted measuring staff in the
 * right hand; grey hair tied back in a knot, a narrow, weathered face that measures everything.
 */
/**
 * Orla. `fight`: as she comes to the dragon's fight (the Turtle's last chapter, La Quemadura done): her staff slung
 * across her back, the map tube kept, and a short wayfarer's bow in her left hand; no pose of her own (the fight's
 * poses move her like any hero).
 */
const orla = (o?: BaseOpts & { fight?: boolean }): Model => {
  const fight = !!o?.fight;
  const c = begin(o, 0.6, 79);
  const cloak = cloth('#5a5248');
  const tunic = cloth('#6a6250');
  const skin = std('#b98e70', { roughness: 0.85 });
  const ash = std('#8a8580', { roughness: 1 });
  const f = figure({ legs: cloth('#4a463e'), boots: ash, torso: tunic, shoulders: leather('#4e3e30'), arms: tunic, forearms: leather('#5a4836'), hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The cloak: open at the front, falling to the calves, its hem worn and ash-pale.
  const back = M(new THREE.CylinderGeometry(0.22, 0.33, 1.12, 12, 1, true, Math.PI * 0.6, Math.PI * 0.8 + Math.PI * 0.4), std('#5a5248', { roughness: 1, side: THREE.DoubleSide }), b);
  back.position.set(0, 0.92, -0.01);
  M(new THREE.CylinderGeometry(0.335, 0.335, 0.08, 12, 1, true, Math.PI * 0.6, Math.PI * 1.2), std('#8e877c', { roughness: 1, side: THREE.DoubleSide }), b).position.set(0, 0.4, -0.01);
  // A hood fallen back on the shoulders.
  const hood = M(new THREE.SphereGeometry(0.15, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.5), cloak, b);
  hood.position.set(0, 1.42, -0.13);
  hood.rotation.x = -1.2;
  hood.scale.set(1.1, 0.7, 1);
  // Ash up the boots: a paler cuff where it dried.
  for (const k of [-1, 1]) M(new THREE.CylinderGeometry(0.075, 0.07, 0.05, 8), std('#a8a29a', { roughness: 1 }), b).position.set(k * 0.09, 0.42, 0.01);
  // Belt with a pouch of chalks and a little brass compass.
  ring(b, 0.18, 0.025, leather('#3a2c20'), 0.98, 5, 14);
  M(new THREE.BoxGeometry(0.08, 0.09, 0.05), leather('#5a4430'), b).position.set(0.15, 0.92, 0.1);
  const compass = M(new THREE.CylinderGeometry(0.03, 0.03, 0.012, 10), std('#8a7a5a', { metalness: 0.6, roughness: 0.4 }), b);
  compass.position.set(-0.09, 0.95, 0.17);
  compass.rotation.x = Math.PI / 2;
  // The map case across her back: a long leather tube on a strap, capped at both ends.
  const tube = G(b, 0.02, 1.15, -0.2);
  tube.rotation.z = 0.75;
  M(new THREE.CylinderGeometry(0.045, 0.045, 0.62, 10), leather('#6a4a2e'), tube);
  for (const y of [-0.31, 0.31]) M(new THREE.CylinderGeometry(0.05, 0.05, 0.04, 10), leather('#3a2a1c'), tube).position.y = y;
  limb(b, V(-0.16, 1.4, 0.1), V(0.16, 0.95, 0.15), 0.012, 0.012, leather('#3a2a1c'), 4);
  // A rolled map under the left arm, its paper pale with a red line showing at the edge (in a fight, the arm has a bow).
  if (!fight) {
    const roll = G(b, -0.235, 1.1, 0.1);
    roll.rotation.set(0.35, 0, 0.3);
    M(new THREE.CylinderGeometry(0.035, 0.035, 0.36, 10), std('#d8ccb0', { roughness: 0.95 }), roll);
    M(new THREE.CylinderGeometry(0.036, 0.036, 0.02, 10), cloth('#a43a2c'), roll).position.y = 0.06;
  }
  // The measuring staff: knotted every pace, held upright at her right (in a fight, slung across her back).
  const staff = fight ? G(b, -0.05, 0.62, -0.24) : G(b, 0.29, 0.02, 0.1);
  if (fight) staff.rotation.z = -0.62;
  const wood = std('#6b5238', { roughness: 0.9 });
  limb(staff, V(0, 0, 0), V(0, 1.45, 0), 0.016, 0.014, wood, 5);
  for (let i = 1; i < 7; i++) {
    const knot = M(new THREE.TorusGeometry(0.019, 0.006, 4, 8), std('#c9bba0'), staff);
    knot.position.set(0, i * 0.2, 0);
    knot.rotation.x = Math.PI / 2;
  }
  M(new THREE.SphereGeometry(0.025, 6, 5), DSTEEL, staff).position.y = 1.46;
  // Grey hair pulled back into a knot; a narrow, lined face, sharp eyes.
  const hair = std('#a8a39a', { roughness: 1 });
  M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.52), hair, h).position.set(0, 1.645, -0.015);
  M(new THREE.SphereGeometry(0.05, 8, 6), hair, h).position.set(0, 1.66, -0.13);
  M(new THREE.TorusGeometry(0.03, 0.008, 4, 8), leather('#3a2a1c'), h).position.set(0, 1.66, -0.105);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.655, 0.116);
    M(new THREE.BoxGeometry(0.048, 0.01, 0.02), hair, h).position.set(k * 0.045, 1.68, 0.112);
    // Crow's feet and the lines down the cheeks.
    M(new THREE.BoxGeometry(0.004, 0.04, 0.008), std('#8e6a54'), h).position.set(k * 0.07, 1.61, 0.106);
    M(new THREE.BoxGeometry(0.02, 0.004, 0.008), std('#8e6a54'), h).position.set(k * 0.078, 1.655, 0.1);
  }
  const nose = M(new THREE.ConeGeometry(0.017, 0.045, 5), std('#a87a60', { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.13);
  nose.rotation.x = Math.PI / 2;
  M(new THREE.BoxGeometry(0.042, 0.007, 0.01), std('#7a4a3e'), h).position.set(0, 1.588, 0.115);
  if (fight) {
    // A short wayfarer's bow, plain ash wood with a red-wrapped grip, and a few arrows at her hip.
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 12; i++) {
      const a = -1.05 + (i / 12) * 2.1;
      pts.push(V(-0.04, Math.sin(a) * 0.62, Math.cos(a) * 0.2 - 0.16));
    }
    const bow = G(f.lh);
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 16, 0.018, 5), std('#8a6a44', { roughness: 0.8 }), bow);
    limb(bow, pts[0]!, pts[12]!, 0.003, 0.003, std('#d8d0c0'), 3);
    M(new THREE.CylinderGeometry(0.02, 0.02, 0.08, 6), cloth('#a43a2c'), bow).position.set(-0.04, 0, 0.03);
    bow.rotation.y = 0.2;
    const quiver = M(new THREE.CylinderGeometry(0.045, 0.04, 0.36, 8), leather('#4a3826'), b);
    quiver.position.set(0.2, 0.86, -0.06);
    quiver.rotation.z = 0.25;
    for (let i = 0; i < 3; i++) M(new THREE.ConeGeometry(0.02, 0.06, 4), cloth('#a43a2c'), b).position.set(0.215 + (i - 1) * 0.022, 1.07, -0.06 + (i % 2) * 0.02);
    return done(c, 1.78, () => undefined, f.rig);
  }
  f.rig.head.rotation.x = -0.02;
  f.rig.head.rotation.y = -0.1;
  f.rig.armL.rotation.set(-0.4, 0, 0.32);
  f.rig.armR.rotation.set(-0.42, 0, -0.16);
  return done(c, 1.78, () => undefined, f.rig);
};

/** Orla as she fights at the heroes' side (the dragon's fight, La Quemadura done): rigged, posed like a hero. */
export const orlaFighter = (o?: BaseOpts): Model => orla({ ...o, fight: true });

/**
 * Pell, Musgoviejo's well-keeper (El pozo, docs/SAGAS.md): old and wiry, a little stooped, sleeves rolled to the elbow
 * over sinewy forearms, a leather apron dark with water, a coil of rope over his shoulder, a wooden bucket with iron
 * hoops in his left hand; a flat cap, a grey stubble, a long, worried face.
 */
const pell = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 83);
  const shirt = cloth('#8c8577');
  const skin = std('#b8866a', { roughness: 0.85 });
  const f = figure({ legs: cloth('#3e3a33'), boots: leather('#2a201a'), torso: shirt, arms: shirt, forearms: skin, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The sleeves rolled up: a thick cuff of cloth at each elbow.
  for (const arm of [f.rig.armL, f.rig.armR]) {
    const cuff = M(new THREE.TorusGeometry(0.05, 0.022, 5, 10), shirt, arm);
    cuff.position.set(0, -0.26, 0);
    cuff.rotation.x = Math.PI / 2;
  }
  // A leather apron, darker where the water got it, its strings round the waist.
  const apron = M(new THREE.BoxGeometry(0.3, 0.52, 0.02), leather('#4a3424'), b);
  apron.position.set(0, 0.9, 0.17);
  M(new THREE.BoxGeometry(0.28, 0.16, 0.022), leather('#33241a'), b).position.set(0, 0.72, 0.172);
  ring(b, 0.175, 0.012, leather('#2e2018'), 1.06, 4, 12);
  // The rope coiled over his right shoulder and across to the left hip.
  const hemp = std('#9a8566', { roughness: 1 });
  const coil = M(new THREE.TorusGeometry(0.2, 0.026, 6, 16), hemp, b);
  coil.position.set(0.02, 1.2, 0.0);
  coil.rotation.set(Math.PI / 2 - 0.15, 0.6, 0.7);
  const coil2 = M(new THREE.TorusGeometry(0.19, 0.02, 6, 16), hemp, b);
  coil2.position.set(0.03, 1.21, 0.0);
  coil2.rotation.set(Math.PI / 2 - 0.1, 0.62, 0.75);
  // The bucket, hanging from the left hand: staves, two iron hoops, a rope handle.
  const bucket = G(f.rig.armL, 0.02, -0.6, 0.02);
  const wood = std('#6a4e34', { roughness: 0.9 });
  const iron = metal('#3a3d44', { roughness: 0.55, metalness: 0.6 });
  M(new THREE.CylinderGeometry(0.1, 0.085, 0.17, 10), wood, bucket).position.y = -0.14;
  for (const y of [-0.08, -0.19]) M(new THREE.TorusGeometry(y > -0.1 ? 0.1 : 0.088, 0.008, 4, 14), iron, bucket).position.y = y;
  M(new THREE.CylinderGeometry(0.088, 0.088, 0.005, 10), std('#141018'), bucket).position.y = -0.06;
  const handle = M(new THREE.TorusGeometry(0.09, 0.008, 4, 12, Math.PI), hemp, bucket);
  handle.position.y = -0.06;
  // An old man's long face: grey stubble, deep-set eyes under heavy brows, a big nose, a mouth set worried.
  const grey = std('#8f8a82', { roughness: 1 });
  M(new THREE.SphereGeometry(0.1, 10, 6, 0, Math.PI * 2, Math.PI * 0.55, Math.PI * 0.3), grey, h).position.set(0, 1.64, 0.0);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.66, 0.116);
    M(new THREE.BoxGeometry(0.048, 0.014, 0.02), grey, h).position.set(k * 0.045, 1.684, 0.112);
    M(new THREE.BoxGeometry(0.03, 0.05, 0.03), grey, h).position.set(k * 0.12, 1.66, 0.0);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.055, 5), std('#b07a5e', { roughness: 0.8 }), h);
  nose.position.set(0, 1.625, 0.135);
  nose.rotation.x = Math.PI / 2;
  const mouth = M(new THREE.BoxGeometry(0.045, 0.008, 0.01), std('#6a3a34'), h);
  mouth.position.set(0, 1.585, 0.115);
  mouth.rotation.z = -0.08;
  // The flat cap, its peak pulled low.
  const felt = cloth('#4a4a44');
  M(new THREE.CylinderGeometry(0.135, 0.13, 0.06, 14), felt, h).position.set(0, 1.74, -0.005);
  const top = M(new THREE.CylinderGeometry(0.14, 0.135, 0.02, 14), felt, h);
  top.position.set(0, 1.775, 0.01);
  top.rotation.x = 0.12;
  const peak = M(new THREE.BoxGeometry(0.17, 0.014, 0.09), felt, h);
  peak.position.set(0, 1.725, 0.13);
  peak.rotation.x = 0.25;
  // A little stooped, his head forward, the bucket's weight in his left arm, the right hand open as if to show it.
  f.rig.head.rotation.x = 0.12;
  f.rig.armL.rotation.set(0.05, 0, 0.12);
  f.rig.armR.rotation.set(-0.75, 0, -0.25);
  return done(c, 1.75, () => undefined, f.rig);
};

/**
 * Yorra, a fisher from the stilt-houses on the titan's fore-fin (La Orilla, docs/SAGAS.md): thin and weathered, in a
 * dark-green oilskin coat hanging open, sleeves rolled over wiry forearms, sea boots; a gutting knife at her belt, a
 * coil of net over her left shoulder with its cork floats, her dark hair tied back hard; a narrow sun-dark face.
 */
const yorra = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 89);
  const oil = std('#2f4a3e', { roughness: 0.38, metalness: 0.1 });
  const oilD = std('#22362e', { roughness: 0.45 });
  const shirt = cloth('#7a7466');
  const skin = std('#a87454', { roughness: 0.85 });
  const f = figure({ legs: cloth('#3a3a36'), boots: std('#1c201e', { roughness: 0.5, metalness: 0.1 }), torso: shirt, arms: oil, forearms: skin, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The oilskin coat, hanging open to the knees, stiff and glossy with salt.
  const coat = M(new THREE.CylinderGeometry(0.2, 0.3, 0.86, 12, 1, true, Math.PI * 0.62, Math.PI * 1.76), std('#2f4a3e', { roughness: 0.38, metalness: 0.1, side: THREE.DoubleSide }), b);
  coat.position.set(0, 1.0, 0);
  M(new THREE.CylinderGeometry(0.305, 0.305, 0.04, 12, 1, true, Math.PI * 0.62, Math.PI * 1.76), std('#1c2a24', { roughness: 0.5, side: THREE.DoubleSide }), b).position.set(0, 0.58, 0);
  // The collar turned up.
  const collar = M(new THREE.CylinderGeometry(0.11, 0.15, 0.08, 10, 1, true, Math.PI * 0.75, Math.PI * 1.5), std('#22362e', { roughness: 0.45, side: THREE.DoubleSide }), b);
  collar.position.set(0, 1.43, 0);
  // Sleeves rolled to the elbow.
  for (const arm of [f.rig.armL, f.rig.armR]) {
    const cuff = M(new THREE.TorusGeometry(0.052, 0.024, 5, 10), oilD, arm);
    cuff.position.set(0, -0.26, 0);
    cuff.rotation.x = Math.PI / 2;
  }
  // The belt, and the gutting knife at the right hip: a short bone grip, a thin blade in a leather sheath.
  ring(b, 0.17, 0.02, leather('#2e2218'), 1.0, 4, 12);
  const knife = G(b, 0.15, 0.94, 0.1);
  knife.rotation.z = 0.15;
  M(new THREE.BoxGeometry(0.035, 0.15, 0.02), leather('#4a3424'), knife).position.y = -0.08;
  M(new THREE.CylinderGeometry(0.014, 0.014, 0.08, 6), std('#d8ccb0', { roughness: 0.7 }), knife).position.y = 0.04;
  // The net coiled over the left shoulder: loops of grey-green cord with cork floats.
  const cord = std('#5a6252', { roughness: 0.95 });
  for (let i = 0; i < 3; i++) {
    const loop = M(new THREE.TorusGeometry(0.17 + i * 0.02, 0.018, 5, 16), cord, b);
    loop.position.set(-0.06, 1.18 - i * 0.02, 0.0);
    loop.rotation.set(Math.PI / 2 - 0.2, -0.6, -0.7 + i * 0.08);
  }
  const cork = std('#b08a5a', { roughness: 0.8 });
  for (const [x, y, z] of [[-0.2, 1.3, 0.1], [0.08, 1.06, 0.14], [-0.18, 1.0, -0.08]] as const) M(new THREE.SphereGeometry(0.028, 6, 5), cork, b).position.set(x, y, z);
  // A loose end of net hanging from the coil.
  limb(b, V(-0.2, 1.05, 0.12), V(-0.24, 0.72, 0.16), 0.01, 0.008, cord, 4);
  limb(b, V(-0.16, 1.04, 0.13), V(-0.18, 0.76, 0.18), 0.01, 0.008, cord, 4);
  // Dark hair tied back hard into a short tail.
  const hair = std('#2a2420', { roughness: 1 });
  M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), hair, h).position.set(0, 1.645, -0.012);
  const tail = M(new THREE.CylinderGeometry(0.03, 0.012, 0.16, 6), hair, h);
  tail.position.set(0, 1.6, -0.17);
  tail.rotation.x = 0.5;
  M(new THREE.TorusGeometry(0.028, 0.008, 4, 8), leather('#3a2a1c'), h).position.set(0, 1.66, -0.13);
  // A narrow, sun-dark face: dark eyes, straight brows, a mouth set hard, a scar on the chin.
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.043, 1.655, 0.116);
    M(new THREE.BoxGeometry(0.046, 0.01, 0.02), hair, h).position.set(k * 0.045, 1.678, 0.112);
  }
  const nose = M(new THREE.ConeGeometry(0.016, 0.045, 5), std('#9a6a4c', { roughness: 0.8 }), h);
  nose.position.set(0, 1.625, 0.132);
  nose.rotation.x = Math.PI / 2;
  M(new THREE.BoxGeometry(0.042, 0.007, 0.01), std('#6a3a34'), h).position.set(0, 1.588, 0.114);
  M(new THREE.BoxGeometry(0.004, 0.022, 0.006), std('#c8a088'), h).position.set(0.015, 1.565, 0.11);
  // Standing square, the right hand on the knife's grip, the left steadying the coil.
  f.rig.armR.rotation.set(-0.15, 0, -0.2);
  f.rig.armL.rotation.set(-0.55, 0, 0.35);
  f.rig.head.rotation.y = -0.1;
  return done(c, 1.74, () => undefined, f.rig);
};

/** The saga people's miniatures, posed for a portrait. */
export const npcModel = (id: NpcId, o?: BaseOpts): Model => {
  switch (id) {
    case 'brann':
      return brann(o);
    case 'odna':
      return odna(o);
    case 'hilde':
      return hilde(o);
    case 'gaspar':
      return gaspar(o);
    case 'maelis':
      return maelis(o);
    case 'fenn':
      return fenn(o);
    case 'mada':
      return mada(o);
    case 'ulla':
      return ulla(o);
    case 'cal':
      return cal(o);
    case 'orla':
      return orla(o);
    case 'dorn':
      return dorn(o);
    case 'pell':
      return pell(o);
    case 'yorra':
      return yorra(o);
  }
};

/* ---------- the 24 unique pieces ---------- */

/** Rune color of each rarity as the UI paints it (cards, borders, the loot reveal). Unique is dragon red. */
export const RARITY_COLOR = { common: '#c4c8d2', uncommon: '#47d16a', rare: '#3a8bff', unique: '#ff4f3a' } as const;
/** The lighter red a unique piece glows with. */
export const UNIQUE_GLOW = '#ff8a6e';

/**
 * One of the 24 unique pieces (packages/content UNIQUES), for its card: what the piece is (a blade, a
 * key, a mantle, a fang...) and, on every one, runes burning in dragon red. Turns slowly on its stand.
 */
export const uniqueItem = (o: BaseOpts & { id: UniqueId }): Model => {
  const c = begin(o, 0.55, 300 + o.id.length * 7);
  const { g, rng } = c;
  const def = UNIQUES[o.id];
  const it = G(g, 0, 0.5, 0);
  const rune = emis('#ff6a50', '#ff2a10', 1.6);
  const hot = emis('#ff8a6e', '#ff3a1a', 2.4, { flatShading: true });
  /** The radius of the torso below at height y (armour on its stand). */
  const torsoR = (y: number): number => (y < -0.15 ? 0.2 : y < 0.05 ? 0.17 + (y + 0.15) * 0.25 : y < 0.2 ? 0.22 : 0.22 - (y - 0.2) * 1.4);
  /** A torso on its stand: waist, chest and shoulders, flattened front to back. */
  const torso = (mat: THREE.Material, k = 1): THREE.Mesh => {
    const pts: THREE.Vector2[] = [];
    for (let i = 0; i <= 12; i++) {
      const y = -0.3 + i * 0.05;
      pts.push(new THREE.Vector2(Math.max(0.06, torsoR(y)) * k, y));
    }
    const m = M(new THREE.LatheGeometry(pts, 16), mat, it);
    m.scale.z = 0.68;
    return m;
  };
  const runes = (parent: THREE.Object3D, from: THREE.Vector3, to: THREE.Vector3, n: number, w = 0.018, hgt = 0.04): void => {
    for (let i = 0; i < n; i++) {
      const p = from.clone().lerp(to, n === 1 ? 0.5 : i / (n - 1));
      M(new THREE.BoxGeometry(w, hgt, 0.012), rune, parent).position.copy(p);
    }
  };
  const gem = (parent: THREE.Object3D, p: THREE.Vector3, r = 0.04): void => {
    M(new THREE.OctahedronGeometry(r), hot, parent).position.copy(p);
    glowSprite(parent, '#ff4f3a', r * 9, p.clone().setZ(p.z + 0.02), 0.65);
  };
  const flames: Flame[] = [];
  let height = 1;
  /** A cord for an amulet of the panoply, and the group its pendant hangs from. */
  const pendant = (): THREE.Group => {
    const cord = G(it, 0, 0.06, 0);
    for (let i = 0; i < 9; i++) {
      const a = (i / 8) * Math.PI * 1.2 - Math.PI * 0.6;
      M(new THREE.SphereGeometry(0.011, 4, 3), DSTEEL, cord).position.set(Math.sin(a) * 0.1, (1 - Math.cos(a)) * 0.12, 0);
    }
    return G(it, 0, -0.02, 0);
  };
  switch (o.id) {
    /* the panoply: the warrior's (docs/PANOPLIA.md §10) */
    case 'butcherCleaver': {
      // The butcher's cleaver: a broad square blade with a hole by its spine, a short dark haft, the edge's runes red.
      limb(it, V(0, -0.5, 0), V(0, -0.08, 0), 0.026, 0.03, DWOOD, 6);
      ring(it, 0.032, 0.008, DSTEEL, -0.1, 4, 12);
      const blade = metal('#7d838b', { roughness: 0.3, flatShading: true });
      const b0 = M(new THREE.BoxGeometry(0.3, 0.42, 0.018), blade, it);
      b0.position.set(0.1, 0.12, 0);
      M(new THREE.TorusGeometry(0.028, 0.008, 4, 12), DSTEEL, it).position.set(0.17, 0.26, 0);
      M(new THREE.BoxGeometry(0.012, 0.42, 0.022), hot, it).position.set(0.255, 0.12, 0);
      runes(it, V(0.0, -0.02, 0.011), V(0.0, 0.28, 0.011), 4);
      gem(it, V(0, -0.52, 0.02), 0.032);
      it.rotation.z = -0.15;
      break;
    }
    case 'barracksMace': {
      // The barracks mace: a straight iron-shod haft and a head of six flanges, worn bright where it struck the drill posts.
      limb(it, V(0, -0.55, 0), V(0, 0.18, 0), 0.022, 0.026, DWOOD, 6);
      for (const y of [-0.4, 0.05]) ring(it, 0.028, 0.008, DSTEEL, y, 4, 12);
      const head = metal('#6a7078', { roughness: 0.35, flatShading: true });
      M(new THREE.CylinderGeometry(0.06, 0.06, 0.2, 8), head, it).position.y = 0.3;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const f = M(new THREE.BoxGeometry(0.1, 0.2, 0.016), head, it);
        f.position.set(Math.cos(a) * 0.08, 0.3, Math.sin(a) * 0.08);
        f.rotation.y = -a;
      }
      M(new THREE.ConeGeometry(0.05, 0.08, 8), head, it).position.y = 0.44;
      gem(it, V(0, 0.3, 0.07), 0.035);
      runes(it, V(0, -0.3, 0.027), V(0, -0.05, 0.027), 3);
      break;
    }
    case 'wellHook': {
      // The well hook: a long iron rod with a ring at the top, a heavy hook at the end, its point burning red.
      limb(it, V(0, -0.1, 0), V(0, 0.5, 0), 0.016, 0.016, DSTEEL, 5);
      ring(it, 0.05, 0.012, DSTEEL, 0.55, 4, 14).rotation.x = Math.PI / 2;
      for (let i = 0; i < 3; i++) ring(it, 0.03, 0.009, DSTEEL, 0.62 + i * 0.05, 4, 10).rotation.y = i % 2 ? Math.PI / 2 : 0;
      const curve = new THREE.CatmullRomCurve3([V(0, -0.1, 0), V(0, -0.32, 0), V(0.12, -0.42, 0), V(0.2, -0.3, 0)]);
      M(new THREE.TubeGeometry(curve, 14, 0.022, 6), metal('#5c6168', { roughness: 0.4 }), it);
      const tip = M(new THREE.ConeGeometry(0.028, 0.08, 5), hot, it);
      tip.position.set(0.21, -0.25, 0);
      runes(it, V(0, 0.05, 0.017), V(0, 0.4, 0.017), 4, 0.014, 0.03);
      gem(it, V(0, 0.55, 0.02), 0.03);
      break;
    }
    /* armours */
    case 'drillPlate':
    case 'frontRankCuirass':
    case 'criersCuirass': {
      const yard = o.id === 'drillPlate';
      const crier = o.id === 'criersCuirass';
      const pl = metal(yard ? '#6d6458' : crier ? '#7c8088' : '#575d66', { roughness: yard ? 0.6 : 0.35, flatShading: true });
      torso(pl);
      for (const k of [-1, 1]) {
        const pa = pauldron(it, k, pl, yard ? 1.0 : crier ? 1.15 : 1.5, yard ? 1 : 2, DSTEEL);
        pa.position.set(k * 0.23, 0.2, 0);
      }
      if (yard) {
        // The drill-yard breastplate: the target painted on its chest, red rings on the dented iron.
        for (const [r, m] of [[0.11, rune], [0.07, DSTEEL], [0.035, rune]] as const) ring(it, r, 0.01, m, 0, 4, 20).position.set(0, 0.04, 0.17);
        for (const [x, y] of [[0.08, 0.12], [-0.1, -0.04], [0.04, -0.16], [-0.05, 0.16]] as const) M(new THREE.SphereGeometry(0.022, 5, 4), DSTEEL, it).position.set(x, y, 0.155);
        gem(it, V(0, 0.04, 0.18), 0.03);
      } else if (crier) {
        // The crier's cuirass: a high gorget for the voice to rise over, a red sash from the shoulder.
        const gorget = M(new THREE.CylinderGeometry(0.11, 0.15, 0.1, 12, 1, true), metal('#9aa0a8', { roughness: 0.25 }), it);
        gorget.position.y = 0.3;
        runes(it, V(-0.09, 0.3, 0.12), V(0.09, 0.3, 0.12), 4, 0.016, 0.03);
        new Ribbon(it, [V(0.2, 0.22, 0.1), V(0.05, 0.02, 0.18), V(-0.12, -0.2, 0.15)], 0.03, cloth('#7a2328'), rng, 0.01);
        gem(it, V(0, 0.1, 0.17), 0.04);
      } else {
        // The front rank's cuirass: heavy and plain, a row of rivets down each side and a red bar across.
        for (const k of [-1, 1]) for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.014, 5, 4), DSTEEL, it).position.set(k * 0.12, 0.16 - i * 0.07, 0.15);
        M(new THREE.BoxGeometry(0.2, 0.03, 0.02), hot, it).position.set(0, 0.08, 0.168);
        runes(it, V(-0.06, -0.08, 0.16), V(0.06, -0.08, 0.16), 3, 0.02, 0.04);
        gem(it, V(0, 0.08, 0.18), 0.035);
      }
      height = 0.85;
      break;
    }
    /* amulets */
    case 'menderThimble': {
      // The mender's thimble: a dimpled thimble on the cord, a needle through red thread wound round it.
      const pd = pendant();
      const th = M(new THREE.CylinderGeometry(0.055, 0.065, 0.11, 12), metal('#a08f6a', { roughness: 0.35 }), pd);
      th.position.y = -0.08;
      M(new THREE.SphereGeometry(0.055, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), metal('#a08f6a', { roughness: 0.35 }), pd).position.y = -0.025;
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        M(new THREE.SphereGeometry(0.008, 4, 3), DSTEEL, pd).position.set(Math.cos(a) * 0.061, -0.08 + (i % 2) * 0.03, Math.sin(a) * 0.061);
      }
      ring(pd, 0.07, 0.01, cloth('#8a2a2a'), -0.12, 4, 16);
      const n = limb(pd, V(-0.12, -0.2, 0.02), V(0.12, 0.02, 0.02), 0.005, 0.003, STEEL, 3);
      n.position.z = 0.02;
      gem(pd, V(0, -0.16, 0.07), 0.028);
      break;
    }
    case 'bellNail': {
      // The bell nail: a long square nail and, hanging from its head, a small bell; a red rune down the nail.
      const pd = pendant();
      M(new THREE.BoxGeometry(0.03, 0.32, 0.03), DSTEEL, pd).position.y = -0.18;
      M(new THREE.BoxGeometry(0.07, 0.025, 0.07), DSTEEL, pd).position.y = -0.02;
      M(new THREE.ConeGeometry(0.02, 0.05, 4), DSTEEL, pd).position.y = -0.36;
      const pts: THREE.Vector2[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8;
        pts.push(new THREE.Vector2(0.025 + t * t * 0.045, -t * 0.09));
      }
      const bell = M(new THREE.LatheGeometry(pts, 12), metal('#8a7a5a', { roughness: 0.3 }), pd);
      bell.position.set(0.07, -0.05, 0);
      runes(pd, V(0, -0.08, 0.016), V(0, -0.3, 0.016), 4, 0.014, 0.024);
      gem(pd, V(0.07, -0.13, 0), 0.022);
      break;
    }
    case 'veteranMedallion': {
      // The veteran's medallion: an old worn disc with notches in its rim, one for each Tide, a red stone in the middle.
      const pd = pendant();
      const d = M(new THREE.CylinderGeometry(0.12, 0.12, 0.028, 18), metal('#7a7468', { roughness: 0.55 }), pd);
      d.rotation.x = Math.PI / 2;
      d.position.y = -0.1;
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const nb = M(new THREE.BoxGeometry(0.022, 0.03, 0.03), DSTEEL, pd);
        nb.position.set(Math.cos(a) * 0.12, -0.1 + Math.sin(a) * 0.12, 0);
        nb.rotation.z = a;
      }
      ring(pd, 0.08, 0.008, rune, -0.1, 4, 18).rotation.x = 0;
      gem(pd, V(0, -0.1, 0.02), 0.04);
      break;
    }
    /* the panoply: the druid's (docs/PANOPLIA.md §10) */
    case 'hiveStaff':
    case 'herbalistStaff':
    case 'briarStaff': {
      // A crooked staff of dark wood; at its head, the hive, the herbs or the briars.
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8;
        pts.push(V(Math.sin(t * 6) * 0.02, -0.55 + t * 0.9, Math.cos(t * 5) * 0.015));
      }
      const bark = std(o.id === 'briarStaff' ? '#3c2a20' : '#4a3424', { roughness: 0.95 });
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 18, 0.024, 6), bark, it);
      if (o.id === 'hiveStaff') {
        // The beekeeper's: a skep of straw bound in rings, its door glowing, three wasps of ember around it.
        const straw = std('#8a7448', { roughness: 0.9, flatShading: true });
        for (let i = 0; i < 4; i++) ring(it, 0.075 - i * 0.012, 0.022, straw, 0.38 + i * 0.045, 5, 12);
        M(new THREE.SphereGeometry(0.035, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), straw, it).position.y = 0.53;
        M(new THREE.CircleGeometry(0.022, 8), hot, it).position.set(0, 0.4, 0.078);
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2;
          M(new THREE.SphereGeometry(0.012, 4, 3), hot, it).position.set(Math.cos(a) * 0.14, 0.45 + i * 0.03, Math.sin(a) * 0.14);
        }
        gem(it, V(0, 0.3, 0.03), 0.03);
      } else if (o.id === 'herbalistStaff') {
        // The herbalist's: a bunch of drying herbs tied under the head, a pouch of spores and a red cord.
        const leaf = std('#4f6a3a', { roughness: 1, flatShading: true });
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2;
          const l = M(new THREE.ConeGeometry(0.025, 0.16, 4), leaf, it);
          l.position.set(Math.cos(a) * 0.04, 0.3, Math.sin(a) * 0.04);
          l.rotation.set(Math.sin(a) * 0.5, 0, Math.PI - Math.cos(a) * 0.5);
        }
        ring(it, 0.03, 0.008, cloth('#8a2a2a'), 0.38, 4, 10);
        const pouch = M(new THREE.SphereGeometry(0.05, 8, 6), leather('#5a4430'), it);
        pouch.position.set(0.07, 0.15, 0.02);
        pouch.scale.y = 1.2;
        runes(it, V(0, -0.3, 0.022), V(0, 0.0, 0.022), 3);
        gem(it, V(0, 0.42, 0), 0.035);
      } else {
        // The briar cutter's: a crook of briars curled at the head, its thorns burning red.
        const curl = new THREE.CatmullRomCurve3([V(0, 0.34, 0), V(0.02, 0.46, 0), V(0.12, 0.5, 0), V(0.16, 0.4, 0), V(0.1, 0.33, 0)]);
        M(new THREE.TubeGeometry(curl, 12, 0.02, 5), bark, it);
        for (let i = 0; i < 9; i++) {
          const pt = i < 5 ? curl.getPoint(i / 4) : pts[1 + (i - 5) * 2]!;
          const th = M(new THREE.ConeGeometry(0.01, 0.045, 4), i % 2 ? hot : DWOOD, it);
          th.position.copy(pt).add(V(0.02, 0, 0.012));
          th.rotation.z = -1.2 + (i % 3) * 0.6;
        }
        gem(it, V(0.1, 0.42, 0.02), 0.03);
      }
      break;
    }
    case 'poulticeTunic':
    case 'goatherdPelt':
    case 'gardenerPlate': {
      const tunic = o.id === 'poulticeTunic';
      const pelt = o.id === 'goatherdPelt';
      torso(tunic ? cloth('#5a5a48') : pelt ? leather('#4a3a2c') : metal('#5d6650', { roughness: 0.5, flatShading: true }));
      if (tunic) {
        // The poultice maker's tunic: a belt of small pouches and strips of linen tied at the chest.
        ring(it, 0.19, 0.016, LEATHER_BELT, -0.12, 4, 18).scale.z = 0.68;
        for (let i = 0; i < 4; i++) {
          const a = -0.9 + i * 0.6;
          M(new THREE.BoxGeometry(0.05, 0.06, 0.03), leather('#6a5038'), it).position.set(Math.sin(a) * 0.19, -0.16, Math.cos(a) * 0.13);
        }
        for (const k of [-1, 1]) new Ribbon(it, [V(k * 0.12, 0.2, 0.12), V(0, 0.08, 0.16), V(-k * 0.1, -0.02, 0.15)], 0.022, cloth('#c8c2b0'), rng, 0.006);
        runes(it, V(-0.06, 0.12, 0.15), V(0.06, 0.12, 0.15), 3, 0.016, 0.03);
        gem(it, V(0, -0.16, 0.15), 0.03);
      } else if (pelt) {
        // The goatherd's pelt: a thick shaggy collar over the shoulders and a red-dyed cord across.
        const fur = std('#6a5a48', { roughness: 1, flatShading: true });
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          const tuft = M(new THREE.ConeGeometry(0.04, 0.12, 4), fur, it);
          tuft.position.set(Math.cos(a) * 0.17, 0.24, Math.sin(a) * 0.12);
          tuft.rotation.set(Math.sin(a) * 0.7, 0, -Math.cos(a) * 0.7);
        }
        new Ribbon(it, [V(0.16, 0.2, 0.1), V(0.0, 0.0, 0.16), V(-0.15, -0.18, 0.14)], 0.02, cloth('#7a2328'), rng, 0.008);
        gem(it, V(0, 0.18, 0.15), 0.035);
      } else {
        // The gardener's plate: green-dark iron, thorns along its rim and a vine of red runes down the front.
        for (let i = 0; i < 8; i++) {
          const a = -1.2 + i * 0.34;
          const th = M(new THREE.ConeGeometry(0.014, 0.06, 4), DSTEEL, it);
          th.position.set(Math.sin(a) * 0.2, 0.22, Math.cos(a) * 0.14);
          th.rotation.x = 0.6;
        }
        runes(it, V(0, 0.16, 0.16), V(0, -0.18, 0.15), 5, 0.014, 0.03);
        gem(it, V(0, 0.04, 0.18), 0.035);
      }
      height = 0.85;
      break;
    }
    case 'undertakerPendant': {
      // The undertaker's pendant: a small iron spade on the cord, its blade worn, a red rune on the haft.
      const pd = pendant();
      M(new THREE.BoxGeometry(0.02, 0.14, 0.02), DWOOD, pd).position.y = -0.08;
      const blade = M(new THREE.BoxGeometry(0.1, 0.11, 0.012), metal('#6a6e74', { roughness: 0.5, flatShading: true }), pd);
      blade.position.y = -0.2;
      M(new THREE.ConeGeometry(0.05, 0.04, 4), metal('#6a6e74', { roughness: 0.5 }), pd).position.y = -0.27;
      runes(pd, V(0, -0.04, 0.012), V(0, -0.12, 0.012), 2, 0.012, 0.02);
      gem(pd, V(0, -0.2, 0.012), 0.025);
      break;
    }
    case 'groundStone': {
      // The ground stone: a rough grey stone, cracked through, the cracks glowing red.
      const pd = pendant();
      const st = M(new THREE.DodecahedronGeometry(0.09, 0), stone('#6a665e'), pd);
      st.position.y = -0.11;
      st.scale.set(1, 0.85, 0.7);
      for (const [a, l] of [[0.3, 0.12], [-0.6, 0.1], [1.8, 0.09]] as const) {
        const c2 = M(new THREE.BoxGeometry(0.008, l, 0.01), hot, pd);
        c2.position.set(Math.sin(a) * 0.02, -0.11, 0.062);
        c2.rotation.z = a;
      }
      gem(pd, V(0, -0.11, 0.07), 0.022);
      break;
    }
    case 'orchardAcorn': {
      // The orchard acorn: a big acorn on the cord, its cap of iron scales, a red sprout from the top.
      const pd = pendant();
      const nut = M(new THREE.SphereGeometry(0.06, 10, 8), std('#7a5434', { roughness: 0.6 }), pd);
      nut.position.y = -0.14;
      nut.scale.y = 1.3;
      M(new THREE.SphereGeometry(0.066, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), metal('#4f4a40', { roughness: 0.7, flatShading: true }), pd).position.y = -0.1;
      M(new THREE.ConeGeometry(0.012, 0.05, 4), hot, pd).position.y = -0.02;
      gem(pd, V(0, -0.16, 0.06), 0.025);
      break;
    }
    /* the panoply: the barbarian's */
    case 'shepherdAxe':
    case 'brawlerAxe':
    case 'carterAxe': {
      // The three greataxes: a long haft and a broad bearded head; the shepherd's crescent, the brawler's notched and
      // double, the carter's square and heavy for splitting wood. The edge burns red on all three.
      const shep = o.id === 'shepherdAxe';
      const brawl = o.id === 'brawlerAxe';
      limb(it, V(0, -0.6, 0), V(0, 0.4, 0), 0.024, 0.028, DWOOD, 6);
      for (const y of [-0.45, -0.3]) ring(it, 0.03, 0.008, LEATHER_BELT, y, 4, 10);
      const head = G(it, 0, 0.28, 0);
      const blade = metal(shep ? '#7a8088' : brawl ? '#5e646c' : '#6a6460', { roughness: 0.35, flatShading: true });
      for (const k of brawl ? [-1, 1] : [1]) {
        const sh = new THREE.Shape();
        sh.moveTo(0, 0.08);
        if (shep) {
          sh.quadraticCurveTo(0.2, 0.22, 0.3, 0.12);
          sh.quadraticCurveTo(0.36, -0.06, 0.28, -0.24);
          sh.quadraticCurveTo(0.16, -0.12, 0, -0.08);
        } else if (brawl) {
          sh.lineTo(0.22, 0.16);
          sh.lineTo(0.2, 0.04);
          sh.lineTo(0.25, -0.02);
          sh.lineTo(0.22, -0.16);
          sh.lineTo(0, -0.08);
        } else {
          sh.lineTo(0.26, 0.12);
          sh.lineTo(0.28, -0.18);
          sh.lineTo(0, -0.1);
        }
        sh.closePath();
        const bl = M(new THREE.ExtrudeGeometry(sh, { depth: 0.03, bevelEnabled: true, bevelThickness: 0.008, bevelSize: 0.008, bevelSegments: 1 }), blade, head);
        bl.position.z = -0.015;
        bl.scale.x = k;
        M(new THREE.BoxGeometry(0.012, shep ? 0.3 : 0.24, 0.036), hot, head).position.set(k * (shep ? 0.31 : brawl ? 0.22 : 0.27), -0.03, 0);
      }
      M(new THREE.BoxGeometry(0.07, 0.2, 0.07), DSTEEL, head).position.set(0, 0, 0);
      runes(head, V(shep ? 0.12 : 0.1, 0.04, 0.03), V(shep ? 0.18 : 0.14, -0.1, 0.03), 3, 0.018, 0.03);
      gem(head, V(0, 0, 0.045), 0.03);
      it.rotation.z = -0.12;
      break;
    }
    case 'foremanPelt':
    case 'challengerHide':
    case 'ancestorPelts': {
      // The barbarian's armours on their stand: leather and fur. The foreman's pelt with a thick collar, the challenger's
      // bare hide crossed by straps, the ancestors' pelts layered, a red-painted hand on each.
      const fore = o.id === 'foremanPelt';
      const chal = o.id === 'challengerHide';
      const hide = leather(chal ? '#5a3a26' : fore ? '#4a3424' : '#3c2c22');
      torso(hide);
      const fur = std(fore ? '#6e6458' : '#57504a', { roughness: 1, flatShading: true });
      if (!chal) {
        const collar = M(new THREE.TorusGeometry(0.17, fore ? 0.07 : 0.05, 5, 12), fur, it);
        collar.rotation.x = Math.PI / 2;
        collar.position.y = 0.24;
        collar.scale.z = 0.7;
      }
      if (o.id === 'ancestorPelts') {
        for (const [y, r] of [[0.05, 0.23], [-0.12, 0.21]] as const) {
          const lay = M(new THREE.CylinderGeometry(r, r + 0.03, 0.1, 10, 1, true), fur, it);
          lay.position.y = y;
          lay.scale.z = 0.7;
        }
      }
      if (chal) {
        // Two straps across the chest and a ring where they meet.
        for (const k of [-1, 1]) {
          const st = M(new THREE.BoxGeometry(0.04, 0.5, 0.02), LEATHER_BELT, it);
          st.position.set(0, 0.03, 0.15);
          st.rotation.z = k * 0.55;
        }
        ring(it, 0.035, 0.01, DSTEEL, 0, 4, 12).position.set(0, 0.03, 0.16);
      }
      for (const k of [-1, 1]) {
        const pa = M(new THREE.SphereGeometry(0.08, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), fur, it);
        pa.position.set(k * 0.22, 0.2, 0);
        pa.scale.set(1.1, 0.7, 0.9);
      }
      // A hand painted in red on the chest.
      M(new THREE.CircleGeometry(0.045, 6), rune, it).position.set(chal ? 0.08 : 0, chal ? -0.12 : -0.02, 0.155);
      for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.012, 0.04, 0.004), rune, it).position.set((chal ? 0.08 : 0) - 0.03 + i * 0.02, (chal ? -0.12 : -0.02) + 0.06, 0.155);
      gem(it, V(0, 0.14, 0.17), 0.034);
      height = 0.85;
      break;
    }
    case 'bullTusk':
    case 'tannerFang': {
      // A curved tooth on the cord: the bull's thick and banded, the tanner's thin and worn smooth, its point red.
      const bull = o.id === 'bullTusk';
      const pd = pendant();
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 6; i++) {
        const t = i / 6;
        pts.push(V(Math.sin(t * 1.4) * (bull ? 0.12 : 0.08), -0.04 - t * (bull ? 0.26 : 0.22), 0));
      }
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, bull ? 0.03 : 0.018, 6), std('#d8ccb0', { roughness: 0.6 }), pd);
      const tip = M(new THREE.ConeGeometry(bull ? 0.03 : 0.018, 0.06, 6), hot, pd);
      tip.position.copy(pts[6]!).add(V(0.01, -0.02, 0));
      tip.rotation.z = Math.PI + 0.5;
      if (bull) for (const y of [-0.07, -0.11]) ring(pd, 0.035, 0.008, DSTEEL, y, 4, 10).position.x = 0.02;
      else runes(pd, V(0.03, -0.1, 0.02), V(0.07, -0.18, 0.02), 3, 0.01, 0.02);
      gem(pd, V(0, -0.03, 0.03), 0.024);
      break;
    }
    case 'wolfSkull': {
      // A small wolf's skull on the cord: the long muzzle, empty sockets with an ember deep in each.
      const pd = pendant();
      const bone = std('#d8ccb0', { roughness: 0.7, flatShading: true });
      const sk = M(new THREE.SphereGeometry(0.075, 8, 6), bone, pd);
      sk.position.y = -0.1;
      sk.scale.set(1, 0.85, 1.1);
      const muz = M(new THREE.ConeGeometry(0.045, 0.14, 6), bone, pd);
      muz.position.set(0, -0.15, 0.08);
      muz.rotation.x = Math.PI / 2 + 0.5;
      for (const k of [-1, 1]) {
        M(new THREE.SphereGeometry(0.016, 5, 4), hot, pd).position.set(k * 0.03, -0.09, 0.065);
        const ear = M(new THREE.ConeGeometry(0.02, 0.05, 4), bone, pd);
        ear.position.set(k * 0.045, -0.03, -0.01);
      }
      glowSprite(pd, '#ff4f3a', 0.25, V(0, -0.09, 0.08), 0.5);
      break;
    }
    /* the panoply: the paladin's */
    case 'firstRoundHammer':
    case 'penitenceHammer':
    case 'keeperHammer': {
      // The three hammers: the first round's a long watchman's maul with a lantern hook, the penitence a nail-head
      // hammer worn by boards, the keeper's a short one with keys hanging from its grip. Light-red runes on each head.
      const first = o.id === 'firstRoundHammer';
      const keeper = o.id === 'keeperHammer';
      limb(it, V(0, first ? -0.6 : -0.45, 0), V(0, 0.3, 0), 0.022, 0.025, DWOOD, 6);
      ring(it, 0.03, 0.008, DSTEEL, -0.25, 4, 10);
      const iron = metal(first ? '#7c838c' : keeper ? '#6a6a70' : '#5f646b', { roughness: 0.3, flatShading: true });
      const hd = M(new THREE.BoxGeometry(first ? 0.32 : 0.26, 0.13, 0.13), iron, it);
      hd.position.y = 0.34;
      if (first) {
        M(new THREE.TorusGeometry(0.04, 0.01, 4, 10), DSTEEL, it).position.set(0.2, 0.34, 0);
        M(new THREE.BoxGeometry(0.018, 0.13, 0.135), hot, it).position.set(-0.16, 0.34, 0);
      } else if (keeper) {
        for (let i = 0; i < 3; i++) {
          const k = G(it, 0.04, -0.42 - i * 0.02, 0.03);
          k.rotation.z = -0.3 + i * 0.3;
          limb(k, V(0, 0, 0), V(0, -0.1, 0), 0.006, 0.006, DSTEEL, 4);
          M(new THREE.TorusGeometry(0.018, 0.005, 3, 8), DSTEEL, k).position.y = 0.01;
        }
        const face = M(new THREE.CylinderGeometry(0.045, 0.045, 0.14, 8), hot, it);
        face.position.set(0.13, 0.34, 0);
        face.rotation.z = Math.PI / 2;
      } else {
        // The claw for pulling nails, opposite the face.
        for (const k of [-1, 1]) {
          const cl = M(new THREE.ConeGeometry(0.02, 0.12, 4), iron, it);
          cl.position.set(-0.17, 0.34 + k * 0.025, 0);
          cl.rotation.z = Math.PI / 2 - k * 0.3;
        }
        const face = M(new THREE.CylinderGeometry(0.055, 0.06, 0.03, 8), hot, it);
        face.position.set(0.14, 0.34, 0);
        face.rotation.z = Math.PI / 2;
      }
      runes(it, V(-0.08, 0.34, 0.066), V(0.08, 0.34, 0.066), 3, 0.016, 0.03);
      gem(it, V(0, 0.1, 0.03), 0.03);
      break;
    }
    case 'armsMasterCuirass':
    case 'armourerCoat':
    case 'stairPlates': {
      // The paladin's armours: the arms master's plate bright and fluted, the armourer's mail full of nicks with a
      // leather apron, the stair plates layered like steps down the front.
      const master = o.id === 'armsMasterCuirass';
      const coat = o.id === 'armourerCoat';
      const pl = metal(master ? '#9aa1aa' : coat ? '#6c7078' : '#7a7f86', { roughness: master ? 0.2 : coat ? 0.6 : 0.35, flatShading: true });
      torso(pl);
      for (const k of [-1, 1]) {
        const pa = pauldron(it, k, pl, master ? 1.4 : 1.1, master ? 3 : 2, DSTEEL);
        pa.position.set(k * 0.23, 0.2, 0);
      }
      if (master) {
        for (let i = -2; i <= 2; i++) M(new THREE.BoxGeometry(0.012, 0.3, 0.01), DSTEEL, it).position.set(i * 0.045, 0.02, 0.165);
        M(new THREE.BoxGeometry(0.03, 0.16, 0.02), hot, it).position.set(0, 0.06, 0.172);
        M(new THREE.BoxGeometry(0.12, 0.03, 0.02), hot, it).position.set(0, 0.1, 0.172);
      } else if (coat) {
        const apron = M(new THREE.BoxGeometry(0.26, 0.3, 0.02), LEATHER_BELT, it);
        apron.position.set(0, -0.12, 0.16);
        for (const [x, y] of [[0.06, 0.12], [-0.09, 0.05], [0.1, -0.02]] as const) M(new THREE.BoxGeometry(0.03, 0.008, 0.01), DSTEEL, it).position.set(x, y, 0.165);
        runes(it, V(-0.06, -0.08, 0.172), V(0.06, -0.08, 0.172), 3, 0.018, 0.035);
      } else {
        for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.3 - i * 0.03, 0.05, 0.03), pl, it).position.set(0, 0.12 - i * 0.07, 0.15 + i * 0.006);
        M(new THREE.BoxGeometry(0.16, 0.012, 0.01), hot, it).position.set(0, -0.12, 0.185);
      }
      gem(it, V(0, master ? 0.06 : 0.16, 0.18), 0.034);
      height = 0.85;
      break;
    }
    case 'blessedStone': {
      // A small square stone from the chapel, bound in iron straps on the cord, a sign cut in it glowing.
      const pd = pendant();
      const st = M(new THREE.BoxGeometry(0.13, 0.15, 0.06), stone('#8a8680'), pd);
      st.position.y = -0.1;
      st.rotation.z = 0.08;
      for (const y of [-0.05, -0.15]) M(new THREE.BoxGeometry(0.14, 0.014, 0.066), DSTEEL, pd).position.y = y;
      M(new THREE.BoxGeometry(0.014, 0.08, 0.004), hot, pd).position.set(0, -0.1, 0.032);
      M(new THREE.BoxGeometry(0.05, 0.014, 0.004), hot, pd).position.set(0, -0.085, 0.032);
      glowSprite(pd, '#ff4f3a', 0.22, V(0, -0.1, 0.05), 0.45);
      break;
    }
    case 'elderCharm': {
      // An old charm: a flat iron ring with a smaller one inside, worn thin, a red bead where they touch.
      const pd = pendant();
      const outer = M(new THREE.TorusGeometry(0.09, 0.016, 5, 16), metal('#7a7468', { roughness: 0.55 }), pd);
      outer.position.y = -0.11;
      const inner = M(new THREE.TorusGeometry(0.05, 0.012, 5, 14), metal('#8a8478', { roughness: 0.45 }), pd);
      inner.position.set(0, -0.15, 0.005);
      gem(pd, V(0, -0.2, 0.02), 0.026);
      runes(pd, V(-0.06, -0.06, 0.018), V(0.06, -0.06, 0.018), 3, 0.012, 0.02);
      break;
    }
    case 'prayerBead': {
      // One big wooden bead from a broken rosary, scorched on one side, a red seam of light through its hole.
      const pd = pendant();
      const bead = M(new THREE.SphereGeometry(0.08, 10, 8), WOOD, pd);
      bead.position.y = -0.11;
      const burnt = M(new THREE.SphereGeometry(0.081, 10, 8, 0, Math.PI * 0.7), DWOOD, pd);
      burnt.position.y = -0.11;
      ring(pd, 0.081, 0.008, rune, -0.11, 4, 16);
      limb(pd, V(0, -0.02, 0), V(0, -0.2, 0), 0.01, 0.01, hot, 4);
      glowSprite(pd, '#ff4f3a', 0.3, V(0, -0.11, 0.06), 0.5);
      break;
    }
    /* the panoply: the ranger's (docs/PANOPLIA.md §10) */
    case 'hunterBow':
    case 'trackerBow':
    case 'pantryBow':
    case 'quarryBow': {
      // A ranger's bow of the old garrison: its limbs, string and grip, an arrow nocked; each its owner's own mark.
      const wood = o.id === 'hunterBow' ? WOOD : o.id === 'quarryBow' ? stone('#6d6a66') : DWOOD;
      const bend = o.id === 'hunterBow' ? 0.13 : o.id === 'pantryBow' ? 0.15 : 0.18;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8 - 0.5;
        pts.push(V(-Math.cos(t * Math.PI) * bend, t * (o.id === 'hunterBow' ? 0.8 : 0.95), 0));
      }
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 16, 0.02, 5), wood, it);
      limb(it, pts[0]!, pts[8]!, 0.004, 0.004, std('#d8d0bd'), 3);
      M(new THREE.CylinderGeometry(0.028, 0.028, 0.12, 6), LEATHER_BELT, it).position.set(-bend, 0, 0);
      limb(it, V(0.02, 0, 0), V(-0.38, 0, 0), 0.007, 0.007, WOOD, 4);
      const head = M(new THREE.ConeGeometry(0.024, 0.07, 4), hot, it);
      head.rotation.z = Math.PI / 2;
      head.position.set(-0.41, 0, 0);
      if (o.id === 'hunterBow') {
        // The hunter's: a hare's tail tied under the grip, grey fletching.
        M(new THREE.SphereGeometry(0.035, 6, 5), std('#b8ab98', { roughness: 1 }), it).position.set(-bend, -0.1, 0);
        for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.06, 0.004, 0.025), std('#8a8a86'), it).position.set(0.03, k * 0.012, 0);
      } else if (o.id === 'trackerBow') {
        // The tracker's: a small dark vial tied to the grip, a drop of red at its lip.
        M(new THREE.CylinderGeometry(0.022, 0.026, 0.07, 6), std('#2a3328', { roughness: 0.3 }), it).position.set(-bend - 0.035, -0.08, 0);
        gem(it, V(-bend - 0.035, -0.035, 0), 0.014);
      } else if (o.id === 'pantryBow') {
        // The supplier's: a coil of sack rope bound round the upper limb.
        for (let i = 0; i < 4; i++) ring(it, 0.03, 0.007, std('#8a7a5c', { roughness: 1 }), 0.18 + i * 0.025, 4, 10).position.x = -bend + 0.02;
      } else {
        // The quarry's: iron caps on both tips, a chisel's notch burning on the grip.
        for (const pt of [pts[0]!, pts[8]!]) M(new THREE.ConeGeometry(0.026, 0.05, 5), DSTEEL, it).position.copy(pt);
        M(new THREE.BoxGeometry(0.05, 0.012, 0.03), hot, it).position.set(-bend, 0.07, 0);
      }
      for (const y of [-0.3, -0.18, 0.18, 0.3]) M(new THREE.BoxGeometry(0.02, 0.02, 0.03), rune, it).position.set(-bend + Math.abs(y) * 0.14, y, 0.015);
      it.rotation.y = Math.PI / 2;
      break;
    }
    case 'falconerJerkin':
    case 'signalerCoat':
    case 'trapperJerkin': {
      const coat = o.id === 'signalerCoat';
      const mat = coat ? cloth('#3a3436') : leather(o.id === 'falconerJerkin' ? '#5a4030' : '#4a3a2a');
      torso(mat, coat ? 1.05 : 1);
      if (o.id === 'falconerJerkin') {
        // The falconer's jerkin: a thick padded shoulder for the bird, a long grey feather tucked in its strap.
        const pad = M(new THREE.SphereGeometry(0.09, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), leather('#3a2a1e'), it);
        pad.position.set(-0.2, 0.22, 0);
        pad.scale.set(1.2, 0.6, 1);
        limb(it, V(-0.12, 0.26, 0.1), V(-0.02, 0.0, 0.17), 0.012, 0.002, std('#9a9890'), 3);
        runes(it, V(0.06, 0.12, 0.16), V(0.06, -0.12, 0.16), 3, 0.016, 0.03);
        gem(it, V(-0.2, 0.25, 0.06), 0.03);
      } else if (coat) {
        // The signaler's coat: long, high-collared, a small ember lantern hooked at the belt.
        const skirt = M(new THREE.CylinderGeometry(0.2, 0.26, 0.24, 12, 1, true), mat, it);
        skirt.position.y = -0.4;
        skirt.scale.z = 0.7;
        M(new THREE.CylinderGeometry(0.12, 0.15, 0.08, 10, 1, true), mat, it).position.y = 0.3;
        M(new THREE.BoxGeometry(0.06, 0.08, 0.06), DSTEEL, it).position.set(0.12, -0.24, 0.13);
        flames.push(flame(it, V(0.12, -0.25, 0.13), 0.35, rng, '#ff4f3a'));
        runes(it, V(-0.08, 0.16, 0.15), V(-0.08, -0.12, 0.15), 4, 0.014, 0.03);
      } else {
        // The trapper's jerkin: a belt of cords and, hung from it, the iron jaws of a small snare.
        ring(it, 0.21, 0.014, LEATHER_BELT, -0.2, 4, 18).scale.z = 0.7;
        for (const k of [-1, 1]) {
          const jaw = M(new THREE.TorusGeometry(0.05, 0.008, 3, 8, Math.PI), DSTEEL, it);
          jaw.position.set(0.12, -0.27, 0.13);
          jaw.rotation.z = k > 0 ? 0 : Math.PI;
        }
        runes(it, V(-0.08, 0.14, 0.16), V(0.08, 0.14, 0.16), 3, 0.018, 0.03);
        gem(it, V(0.12, -0.27, 0.15), 0.025);
      }
      height = 0.85;
      break;
    }
    case 'ballistaTip': {
      // The great ballista's tip: a heavy iron bolt-head on the cord, its socket bound, its point burning.
      const pd = pendant();
      M(new THREE.CylinderGeometry(0.03, 0.04, 0.1, 6), DSTEEL, pd).position.y = -0.06;
      const tip = M(new THREE.ConeGeometry(0.06, 0.2, 4), metal('#6e747c', { roughness: 0.3, flatShading: true }), pd);
      tip.position.y = -0.21;
      tip.rotation.x = Math.PI;
      M(new THREE.ConeGeometry(0.02, 0.06, 4), hot, pd).position.set(0, -0.33, 0);
      runes(pd, V(0, -0.14, 0.045), V(0, -0.24, 0.03), 2, 0.012, 0.024);
      break;
    }
    case 'archerFeather': {
      // The marksman's feather: one long grey feather, its quill bound in red thread.
      const pd = pendant();
      const vane = M(new THREE.ConeGeometry(0.05, 0.36, 4, 1), std('#a8a6a0', { roughness: 1, side: THREE.DoubleSide }), pd);
      vane.position.y = -0.22;
      vane.rotation.x = Math.PI;
      vane.scale.z = 0.15;
      limb(pd, V(0, -0.02, 0), V(0, -0.42, 0.01), 0.006, 0.003, std('#d8d0bd'), 3);
      ring(pd, 0.012, 0.006, rune, -0.05, 3, 8);
      gem(pd, V(0, -0.06, 0.015), 0.02);
      break;
    }
    case 'tunerLens': {
      // The tuner's lens: a brass ring and a disc of glass with a red cross-hair burning in it.
      const pd = pendant();
      const r = M(new THREE.TorusGeometry(0.09, 0.016, 6, 18), metal('#8a7a5a', { roughness: 0.3 }), pd);
      r.position.y = -0.1;
      const glass = M(new THREE.CylinderGeometry(0.08, 0.08, 0.01, 16), emis('#ffd2c8', '#ff4f3a', 0.5, { transparent: true, opacity: 0.55 }), pd);
      glass.rotation.x = Math.PI / 2;
      glass.position.y = -0.1;
      M(new THREE.BoxGeometry(0.12, 0.008, 0.012), hot, pd).position.set(0, -0.1, 0.008);
      M(new THREE.BoxGeometry(0.008, 0.12, 0.012), hot, pd).position.set(0, -0.1, 0.008);
      break;
    }
    /* the panoply: the cleric's */
    case 'nurseStaff':
    case 'watercarrierStaff':
    case 'lamplighterStaff':
    case 'midwifeStaff': {
      // A cleric's staff of the old garrison: a pale haft and, on top, its owner's work.
      const lamp = o.id === 'lamplighterStaff';
      limb(it, V(0, -0.55, 0), V(0, lamp ? 0.5 : 0.3, 0), 0.02, 0.024, WOOD, 6);
      for (const y of [-0.3, 0.05]) ring(it, 0.026, 0.007, DSTEEL, y, 4, 12);
      if (o.id === 'nurseStaff') {
        // The nurse's: bandage wound round its head, a small bowl hung from it.
        for (let i = 0; i < 5; i++) ring(it, 0.035, 0.012, std('#d8d4cc', { roughness: 1 }), 0.22 + i * 0.03, 4, 12).rotation.z = (i % 2 ? 0.2 : -0.2);
        const bowl = M(new THREE.SphereGeometry(0.06, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#8a8478', { roughness: 0.4 }), it);
        bowl.position.set(0.08, 0.2, 0);
        gem(it, V(0, 0.38, 0.02), 0.032);
      } else if (o.id === 'watercarrierStaff') {
        // The water carrier's: a yoke's hook at the top and a small bucket on it.
        const hook = M(new THREE.TorusGeometry(0.05, 0.01, 4, 10, Math.PI), DSTEEL, it);
        hook.position.set(0.05, 0.3, 0);
        const pail = M(new THREE.CylinderGeometry(0.05, 0.04, 0.08, 8), DWOOD, it);
        pail.position.set(0.1, 0.18, 0);
        ring(it, 0.05, 0.006, DSTEEL, 0.21, 4, 10).position.x = 0.1;
        gem(it, V(0.1, 0.22, 0), 0.02);
      } else if (lamp) {
        // The lamplighter's: a tall pole with a hook and a small lamp swinging from it.
        limb(it, V(0, 0.48, 0), V(0.12, 0.5, 0), 0.01, 0.01, DSTEEL, 3);
        M(new THREE.BoxGeometry(0.07, 0.09, 0.07), DSTEEL, it).position.set(0.12, 0.4, 0);
        flames.push(flame(it, V(0.12, 0.38, 0), 0.4, rng, '#ff4f3a'));
        glowSprite(it, '#ffd2c8', 0.35, V(0.12, 0.4, 0), 0.55);
      } else {
        // The midwife's: a crescent at the top and a red ribbon tied below it.
        const moon = M(new THREE.TorusGeometry(0.08, 0.016, 5, 14, Math.PI * 1.3), metal('#a0a4aa', { roughness: 0.3 }), it);
        moon.position.y = 0.38;
        moon.rotation.z = -Math.PI * 0.15;
        new Ribbon(it, [V(0, 0.28, 0.02), V(0.05, 0.18, 0.04), V(0.02, 0.06, 0.05)], 0.018, cloth('#7a2328'), rng, 0.01);
        gem(it, V(0, 0.38, 0.02), 0.03);
      }
      runes(it, V(0, -0.2, 0.024), V(0, 0.0, 0.024), 3, 0.014, 0.03);
      break;
    }
    case 'gardenerHabit':
    case 'cisternStole':
    case 'pilgrimSmock': {
      const habit = o.id === 'gardenerHabit';
      const mat = cloth(habit ? '#4a4438' : o.id === 'pilgrimSmock' ? '#5a5650' : '#3e4248');
      torso(mat, 1.04);
      const skirt = M(new THREE.CylinderGeometry(0.2, 0.25, 0.22, 12, 1, true), mat, it);
      skirt.position.y = -0.4;
      skirt.scale.z = 0.7;
      if (habit) {
        // The gardener's habit: a rope belt and a sprig tucked in it.
        ring(it, 0.2, 0.012, std('#8a7a5c', { roughness: 1 }), -0.2, 4, 18).scale.z = 0.7;
        for (let i = 0; i < 3; i++) limb(it, V(0.1, -0.2, 0.14), V(0.13 + i * 0.03, -0.05 + i * 0.02, 0.15), 0.006, 0.003, std('#4a5a3a'), 3);
        gem(it, V(0, -0.2, 0.15), 0.028);
      } else if (o.id === 'cisternStole') {
        // The cistern's stole: a long dark band over both shoulders, drops of red stitched down it.
        for (const k of [-1, 1]) {
          new Ribbon(it, [V(k * 0.08, 0.3, 0.06), V(k * 0.09, 0.1, 0.16), V(k * 0.09, -0.15, 0.16), V(k * 0.08, -0.42, 0.14)], 0.05, cloth('#1e2a3a'), rng, 0.005);
          runes(it, V(k * 0.09, 0.05, 0.175), V(k * 0.09, -0.3, 0.17), 3, 0.014, 0.022);
        }
        gem(it, V(0, 0.26, 0.12), 0.03);
      } else {
        // The pilgrim's smock: a shell badge on the chest and a strap across for a walking staff.
        new Ribbon(it, [V(0.18, 0.22, 0.08), V(0.0, 0.0, 0.17), V(-0.15, -0.22, 0.14)], 0.025, LEATHER_BELT, rng, 0.005);
        const shell = M(new THREE.SphereGeometry(0.05, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), std('#d8ccb0', { roughness: 0.6 }), it);
        shell.position.set(-0.08, 0.1, 0.16);
        shell.rotation.x = Math.PI / 2;
        gem(it, V(-0.08, 0.1, 0.2), 0.022);
      }
      height = 0.9;
      break;
    }
    case 'bronzeSun': {
      // The bronze sun: a disc of bronze with its rays, a red heart.
      const pd = pendant();
      const bronze = metal('#8a6a48', { roughness: 0.35, flatShading: true });
      const d = M(new THREE.CylinderGeometry(0.08, 0.08, 0.025, 16), bronze, pd);
      d.rotation.x = Math.PI / 2;
      d.position.y = -0.12;
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const ray = M(new THREE.ConeGeometry(0.016, 0.06, 3), bronze, pd);
        ray.position.set(Math.cos(a) * 0.11, -0.12 + Math.sin(a) * 0.11, 0);
        ray.rotation.z = a - Math.PI / 2;
      }
      gem(pd, V(0, -0.12, 0.02), 0.035);
      break;
    }
    case 'infirmaryBell': {
      // The infirmary's little bell: a handbell with a short wooden handle, a red clapper.
      const pd = pendant();
      limb(pd, V(0, -0.02, 0), V(0, -0.1, 0), 0.016, 0.016, DWOOD, 5);
      const pts: THREE.Vector2[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8;
        pts.push(new THREE.Vector2(0.03 + t * t * 0.06, -t * 0.12));
      }
      const bell = M(new THREE.LatheGeometry(pts, 12), metal('#9a9488', { roughness: 0.3 }), pd);
      bell.position.y = -0.1;
      gem(pd, V(0, -0.22, 0), 0.022);
      runes(pd, V(-0.04, -0.17, 0.07), V(0.04, -0.17, 0.07), 3, 0.012, 0.02);
      break;
    }
    case 'vigilMedal': {
      // The vigil's medal: a small round medal with a closed eye on it, worn smooth by a thumb.
      const pd = pendant();
      const d = M(new THREE.CylinderGeometry(0.1, 0.1, 0.022, 16), metal('#8a8e94', { roughness: 0.5 }), pd);
      d.rotation.x = Math.PI / 2;
      d.position.y = -0.1;
      const lid = M(new THREE.TorusGeometry(0.045, 0.008, 3, 10, Math.PI), rune, pd);
      lid.position.set(0, -0.1, 0.014);
      lid.rotation.z = Math.PI;
      ring(pd, 0.1, 0.008, DSTEEL, -0.1, 4, 18).rotation.x = 0;
      gem(pd, V(0, -0.13, 0.016), 0.02);
      break;
    }
    /* the panoply: the artificer's (docs/PANOPLIA.md §10) */
    case 'minerPick': {
      // The miner's pick: a worn haft and a two-pointed iron head, one point burning red where it bit the rock.
      limb(it, V(0, -0.5, 0), V(0, 0.28, 0), 0.022, 0.026, DWOOD, 6);
      ring(it, 0.03, 0.008, DSTEEL, 0.2, 4, 12);
      const head = metal('#5f646c', { roughness: 0.45, flatShading: true });
      M(new THREE.BoxGeometry(0.08, 0.07, 0.05), head, it).position.y = 0.3;
      for (const k of [-1, 1]) {
        const p = M(new THREE.ConeGeometry(0.03, 0.22, 5), k > 0 ? hot : head, it);
        p.position.set(k * 0.15, 0.27, 0);
        p.rotation.z = k * (Math.PI / 2 + 0.25);
      }
      runes(it, V(0, -0.3, 0.025), V(0, 0.08, 0.025), 4);
      gem(it, V(0, 0.3, 0.03), 0.03);
      break;
    }
    case 'stokerPoker': {
      // The stoker's poker: a long iron rod with a ring for the hand and a hooked end still red from the ovens.
      limb(it, V(0, -0.5, 0), V(0, 0.4, 0), 0.014, 0.014, DSTEEL, 5);
      ring(it, 0.05, 0.012, DSTEEL, -0.52, 4, 14).rotation.x = Math.PI / 2;
      limb(it, V(0, 0.4, 0), V(0.09, 0.48, 0), 0.016, 0.012, hot, 4);
      glowSprite(it, '#ff6a3a', 0.4, V(0.06, 0.46, 0), 0.7);
      runes(it, V(0, -0.2, 0.015), V(0, 0.25, 0.015), 4, 0.014, 0.03);
      gem(it, V(0, -0.42, 0.02), 0.03);
      break;
    }
    case 'buildersSquare': {
      // The master builder's square: an iron L marked with notches, a plumb bob hanging from its corner on a red thread.
      const sq = metal('#8a8f96', { roughness: 0.3, flatShading: true });
      M(new THREE.BoxGeometry(0.05, 0.62, 0.016), sq, it).position.set(-0.12, 0.0, 0);
      M(new THREE.BoxGeometry(0.4, 0.05, 0.016), sq, it).position.set(0.06, -0.29, 0);
      for (let i = 0; i < 8; i++) M(new THREE.BoxGeometry(0.02, 0.006, 0.02), DSTEEL, it).position.set(-0.1, -0.22 + i * 0.07, 0.004);
      limb(it, V(-0.12, 0.3, 0), V(0.02, 0.02, 0), 0.003, 0.003, cloth('#8a2a2a'), 3);
      M(new THREE.ConeGeometry(0.03, 0.08, 8), DSTEEL, it).position.set(0.02, -0.02, 0);
      runes(it, V(-0.04, -0.29, 0.01), V(0.22, -0.29, 0.01), 4, 0.02, 0.025);
      gem(it, V(-0.12, -0.29, 0.02), 0.03);
      it.rotation.z = 0.2;
      break;
    }
    case 'clockmakerGear': {
      // The clockmaker's gear: a brass cog with fine teeth on the cord, a smaller one turning in it.
      const pd = pendant();
      const brass = metal('#a08f6a', { roughness: 0.3 });
      const d = M(new THREE.CylinderGeometry(0.1, 0.1, 0.022, 16), brass, pd);
      d.rotation.x = Math.PI / 2;
      d.position.y = -0.1;
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const t = M(new THREE.BoxGeometry(0.026, 0.03, 0.022), brass, pd);
        t.position.set(Math.cos(a) * 0.11, -0.1 + Math.sin(a) * 0.11, 0);
        t.rotation.z = a;
      }
      const s2 = M(new THREE.CylinderGeometry(0.04, 0.04, 0.03, 10), DSTEEL, pd);
      s2.rotation.x = Math.PI / 2;
      s2.position.set(0.04, -0.07, 0.012);
      gem(pd, V(0, -0.1, 0.02), 0.03);
      break;
    }
    case 'locksmithRivet': {
      // The locksmith's rivet: a fat iron rivet through a small key's bow, the key's bit red.
      const pd = pendant();
      M(new THREE.CylinderGeometry(0.06, 0.06, 0.03, 12), DSTEEL, pd).position.y = -0.04;
      M(new THREE.CylinderGeometry(0.022, 0.022, 0.18, 8), DSTEEL, pd).position.y = -0.14;
      const bow = M(new THREE.TorusGeometry(0.05, 0.012, 5, 14), metal('#8a7a5a', { roughness: 0.35 }), pd);
      bow.position.set(0, -0.12, 0);
      limb(pd, V(0.05, -0.12, 0), V(0.18, -0.12, 0), 0.012, 0.012, metal('#8a7a5a', { roughness: 0.35 }), 4);
      M(new THREE.BoxGeometry(0.02, 0.05, 0.014), hot, pd).position.set(0.17, -0.15, 0);
      gem(pd, V(0, -0.04, 0.02), 0.026);
      break;
    }
    case 'rodmanCopper': {
      // The rodman's copper: a coil of copper wire wound round a short iron rod, its point lit red.
      const pd = pendant();
      const copper = metal('#b0683a', { roughness: 0.3 });
      M(new THREE.CylinderGeometry(0.014, 0.014, 0.34, 6), DSTEEL, pd).position.y = -0.16;
      for (let i = 0; i < 6; i++) ring(pd, 0.04, 0.009, copper, -0.06 - i * 0.04, 4, 12);
      M(new THREE.ConeGeometry(0.02, 0.06, 5), hot, pd).position.y = -0.36;
      glowSprite(pd, '#d8d0ff', 0.25, V(0, -0.36, 0), 0.6);
      gem(pd, V(0, -0.02, 0.02), 0.026);
      break;
    }
    case 'gunnerPlate':
    case 'masonApron':
    case 'kennelCoat': {
      const gunner = o.id === 'gunnerPlate';
      const mason = o.id === 'masonApron';
      const pl = gunner ? metal('#4f555e', { roughness: 0.4, flatShading: true }) : mason ? cloth('#6b6258') : leather('#5a3e28');
      torso(pl);
      if (gunner) {
        // The gunner's breastplate: heavy and dark, a bandolier of round shot across it.
        for (const k of [-1, 1]) pauldron(it, k, pl, 1.3, 2, DSTEEL).position.set(k * 0.23, 0.2, 0);
        for (let i = 0; i < 6; i++) M(new THREE.SphereGeometry(0.028, 6, 5), DSTEEL, it).position.set(-0.15 + i * 0.06, 0.14 - i * 0.05, 0.16);
        runes(it, V(-0.08, -0.12, 0.16), V(0.08, -0.12, 0.16), 3, 0.02, 0.04);
        gem(it, V(0, 0.02, 0.18), 0.035);
      } else if (mason) {
        // The mason's apron: a thick leather apron over the shirt, lime-white, a trowel in its pocket.
        const ap = M(new THREE.BoxGeometry(0.3, 0.42, 0.02), leather('#c8c0b0'), it);
        ap.position.set(0, -0.08, 0.16);
        M(new THREE.BoxGeometry(0.12, 0.08, 0.025), leather('#9a9080'), it).position.set(0.06, -0.12, 0.175);
        const tr = M(new THREE.BoxGeometry(0.05, 0.08, 0.008), STEEL, it);
        tr.position.set(0.06, -0.04, 0.18);
        tr.rotation.z = 0.3;
        runes(it, V(-0.1, -0.24, 0.175), V(0.1, -0.24, 0.175), 4, 0.018, 0.03);
        gem(it, V(-0.07, 0.08, 0.18), 0.03);
      } else {
        // The kennelman's jerkin: worn leather with a studded dog collar on its belt and a whistle on a thong.
        for (const k of [-1, 1]) for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.012, 4, 3), DSTEEL, it).position.set(k * 0.11, 0.14 - i * 0.08, 0.155);
        const col = M(new THREE.TorusGeometry(0.07, 0.014, 5, 14), leather('#2e2018'), it);
        col.position.set(0.08, -0.2, 0.16);
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          M(new THREE.SphereGeometry(0.008, 4, 3), STEEL, it).position.set(0.08 + Math.cos(a) * 0.07, -0.2 + Math.sin(a) * 0.07, 0.175);
        }
        M(new THREE.CylinderGeometry(0.01, 0.012, 0.07, 6), metal('#a08f6a', { roughness: 0.3 }), it).position.set(-0.06, 0.02, 0.17);
        gem(it, V(0, 0.1, 0.17), 0.032);
      }
      height = 0.85;
      break;
    }
    /* the panoply: the mage's */
    case 'stormStaff':
    case 'distillerStaff':
    case 'pantryStaff': {
      const storm = o.id === 'stormStaff';
      const still = o.id === 'distillerStaff';
      limb(it, V(0, -0.55, 0), V(0, 0.3, 0), 0.02, 0.026, storm ? DSTEEL : DWOOD, 6);
      if (storm) {
        // The staff of storms: an iron staff forked at its top like a lightning rod, pale sparks between the tines.
        for (const k of [-1, 1]) limb(it, V(0, 0.3, 0), V(k * 0.08, 0.5, 0), 0.012, 0.008, DSTEEL, 4);
        for (const y of [0.38, 0.45]) glowSprite(it, '#bcd4ff', 0.16, V(0, y, 0), 0.9);
        gem(it, V(0, 0.32, 0.02), 0.035);
      } else if (still) {
        // The distiller's staff: a small glass retort bound to its head, a green drop in it, a red stopper.
        const glass = std('#bfe0d0', { roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        M(new THREE.SphereGeometry(0.08, 10, 8), glass, it).position.y = 0.4;
        limb(it, V(0.05, 0.44, 0), V(0.16, 0.52, 0), 0.014, 0.008, glass, 4);
        M(new THREE.SphereGeometry(0.04, 8, 6), emis('#c8ffa8', '#5ad04a', 0.9), it).position.y = 0.38;
        gem(it, V(0, 0.49, 0), 0.026);
        ring(it, 0.03, 0.008, DSTEEL, 0.31, 4, 12);
      } else {
        // The pantry staff: pale wood capped with a cluster of clear ice, a red rune frozen inside it.
        const ice = emis('#eaf8ff', '#7fc8ff', 0.7, { flatShading: true, transparent: true, opacity: 0.9 });
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2;
          const sh = M(new THREE.OctahedronGeometry(0.04), ice, it);
          sh.position.set(Math.cos(a) * 0.04, 0.38 + (i % 2) * 0.04, Math.sin(a) * 0.04);
          sh.scale.y = 2;
        }
        gem(it, V(0, 0.38, 0), 0.03);
      }
      runes(it, V(0, -0.25, 0.026), V(0, 0.15, 0.026), 4);
      break;
    }
    case 'whiteWallRobe':
    case 'teacherRobe':
    case 'messengerCape': {
      const white = o.id === 'whiteWallRobe';
      const teacher = o.id === 'teacherRobe';
      const robe = M(new THREE.CylinderGeometry(0.15, 0.32, 0.72, 14, 1, true, -Math.PI * 0.85, Math.PI * 1.7), std(white ? '#d8e2ea' : teacher ? '#4a5870' : '#5a4a3e', { side: THREE.DoubleSide, roughness: 0.9 }), it);
      robe.position.y = -0.06;
      robe.rotation.y = Math.PI;
      if (white) {
        // The white wall robe: an apprentice's pale robe, frost crusted along its hem.
        const ice = emis('#eaf8ff', '#7fc8ff', 0.7, { flatShading: true, transparent: true, opacity: 0.9 });
        for (let i = 0; i < 9; i++) {
          const a = -2.3 + (i / 8) * 4.6;
          M(new THREE.OctahedronGeometry(0.03), ice, it).position.set(Math.sin(a) * 0.31, -0.4, Math.cos(a) * 0.31);
        }
        gem(it, V(0, 0.27, 0.17), 0.04);
      } else if (teacher) {
        // The teacher's robe: deep blue with a silver collar, a small book hanging from its belt.
        ring(it, 0.16, 0.025, metal('#9aa0a8', { roughness: 0.3 }), 0.3, 5, 16).rotation.x = Math.PI / 2;
        M(new THREE.BoxGeometry(0.1, 0.13, 0.04), leather('#5a2a24'), it).position.set(0.14, -0.06, 0.24);
        gem(it, V(0, 0.27, 0.18), 0.04);
      } else {
        // The messenger's cape: a travelling cape with a hood down and a sealed letter case on a strap.
        const hood = M(new THREE.SphereGeometry(0.14, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), std('#4a3c32', { side: THREE.DoubleSide }), it);
        hood.position.set(0, 0.26, -0.08);
        hood.rotation.x = -0.6;
        const tube = M(new THREE.CylinderGeometry(0.03, 0.03, 0.22, 8), leather('#3a2a1e'), it);
        tube.position.set(-0.12, -0.02, 0.24);
        tube.rotation.z = 0.5;
        M(new THREE.CylinderGeometry(0.032, 0.032, 0.02, 8), hot, it).position.set(-0.18, 0.07, 0.24);
        gem(it, V(0.05, 0.25, 0.17), 0.035);
      }
      runes(it, V(-0.22, -0.36, 0.22), V(0.22, -0.36, 0.22), 5, 0.018, 0.03);
      height = 0.85;
      break;
    }
    case 'jailerSurcoat': {
      // The jailer's surcoat: a dark sleeveless surcoat over mail, a ring of heavy keys at its belt.
      torso(std('#2e2a30', { roughness: 0.9 }));
      M(new THREE.BoxGeometry(0.36, 0.04, 0.3), leather('#3a2a1e'), it).position.y = -0.14;
      const kr = M(new THREE.TorusGeometry(0.05, 0.008, 4, 12), DSTEEL, it);
      kr.position.set(0.12, -0.2, 0.17);
      for (let i = 0; i < 3; i++) limb(it, V(0.12 + (i - 1) * 0.03, -0.24, 0.17), V(0.12 + (i - 1) * 0.04, -0.34, 0.17), 0.008, 0.008, DSTEEL, 3);
      runes(it, V(-0.08, 0.1, 0.16), V(-0.08, -0.06, 0.16), 3, 0.02, 0.035);
      gem(it, V(0, 0.16, 0.17), 0.035);
      height = 0.85;
      break;
    }
    case 'iceTear':
    case 'dreamStone':
    case 'cometEmber': {
      const pd = pendant();
      if (o.id === 'iceTear') {
        // The ice tear: a drop of clear ice that never melts, a red spark at its heart.
        const ice = emis('#eaf8ff', '#7fc8ff', 0.8, { transparent: true, opacity: 0.85 });
        const d = M(new THREE.SphereGeometry(0.07, 12, 10), ice, pd);
        d.position.y = -0.14;
        const tip = M(new THREE.ConeGeometry(0.065, 0.12, 12), ice, pd);
        tip.position.y = -0.05;
        gem(pd, V(0, -0.14, 0), 0.025);
      } else if (o.id === 'dreamStone') {
        // The deep sleep stone: a smooth dark river stone, violet light moving under it, a red vein.
        const st = M(new THREE.SphereGeometry(0.09, 12, 8), stone('#2a2638'), pd);
        st.position.y = -0.12;
        st.scale.set(1, 0.75, 0.6);
        glowSprite(pd, '#9a86d0', 0.3, V(0, -0.12, 0.05), 0.6);
        gem(pd, V(0.02, -0.1, 0.05), 0.022);
      } else {
        // The comet's ember: a black stone burning red within, set in iron, a streak of fire trailing it.
        const e = M(new THREE.DodecahedronGeometry(0.07, 0), hot, pd);
        e.position.y = -0.12;
        const tail = M(new THREE.ConeGeometry(0.05, 0.2, 8), emis('#ffb070', '#ff5a1a', 1.2, { transparent: true, opacity: 0.6 }), pd);
        tail.position.set(0.1, -0.04, 0);
        tail.rotation.z = -2.3;
        ring(pd, 0.075, 0.01, DSTEEL, -0.12, 4, 14).rotation.x = Math.PI / 2;
        gem(pd, V(0, -0.12, 0.06), 0.022);
      }
      break;
    }
    /* weapons */
    case 'gateBlade': {
      // The gate's blade: dark steel, the guard forged as two bars of a portcullis.
      const sw = sword(0.82, metal('#6e747c', { roughness: 0.25 }), DSTEEL);
      it.add(sw);
      sw.position.y = -0.45;
      sw.scale.set(1.7, 1, 1.7);
      for (const k of [-1, 1]) limb(sw, V(k * 0.07, 0.05, 0), V(k * 0.07, 0.2, 0), 0.012, 0.012, DSTEEL, 4);
      runes(sw, V(0, 0.3, 0.008), V(0, 0.82, 0.008), 5);
      gem(sw, V(0, -0.03, 0.02), 0.035);
      break;
    }
    case 'garrisonKey': {
      // The garrison's key: a long iron key, its bow a gear, its teeth burning.
      const k = G(it, 0, 0, 0);
      k.rotation.z = 0.5;
      limb(k, V(0, -0.35, 0), V(0, 0.25, 0), 0.025, 0.025, DSTEEL, 6);
      const bow = M(new THREE.TorusGeometry(0.12, 0.03, 6, 16), DSTEEL, k);
      bow.position.y = 0.37;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        M(new THREE.BoxGeometry(0.04, 0.04, 0.05), DSTEEL, k).position.set(Math.cos(a) * 0.15, 0.37 + Math.sin(a) * 0.15, 0);
      }
      gem(k, V(0, 0.37, 0), 0.045);
      for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.08 - i * 0.015, 0.035, 0.03), i === 1 ? hot : DSTEEL, k).position.set(0.05, -0.3 + i * 0.07, 0);
      runes(k, V(0, -0.15, 0.026), V(0, 0.15, 0.026), 3);
      break;
    }
    case 'fallenStaff': {
      // The fallen master's staff: black wood, a cracked crystal held in iron claws.
      limb(it, V(0, -0.5, 0), V(0, 0.32, 0), 0.022, 0.028, DWOOD, 6);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        limb(it, V(0, 0.3, 0), V(Math.cos(a) * 0.06, 0.48, Math.sin(a) * 0.06), 0.012, 0.008, DSTEEL, 4);
      }
      const cr = M(new THREE.OctahedronGeometry(0.09), emis('#ffc0b0', '#ff4f3a', 1.8, { flatShading: true, transparent: true, opacity: 0.92 }), it);
      cr.position.y = 0.45;
      cr.scale.y = 1.6;
      glowSprite(it, '#ff4f3a', 0.7, V(0, 0.45, 0), 0.6);
      runes(it, V(0, -0.2, 0.027), V(0, 0.2, 0.027), 4);
      break;
    }
    case 'chapelLamp': {
      // The old chapel's lamp: a short iron haft and a lantern with a red-white flame.
      limb(it, V(0, -0.45, 0), V(0, 0.12, 0), 0.02, 0.022, DWOOD, 5);
      M(new THREE.CylinderGeometry(0.1, 0.12, 0.04, 8), DSTEEL, it).position.y = 0.14;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        limb(it, V(Math.cos(a) * 0.1, 0.15, Math.sin(a) * 0.1), V(Math.cos(a) * 0.08, 0.42, Math.sin(a) * 0.08), 0.008, 0.008, DSTEEL, 3);
      }
      M(new THREE.ConeGeometry(0.11, 0.12, 8), DSTEEL, it).position.y = 0.48;
      flames.push(flame(it, V(0, 0.18, 0), 0.8, rng, '#ff4f3a'));
      glowSprite(it, '#ffd2c8', 0.5, V(0, 0.28, 0), 0.6);
      runes(it, V(-0.06, 0.14, 0.11), V(0.06, 0.14, 0.11), 3, 0.014, 0.025);
      break;
    }
    case 'watchBow': {
      // The watchtower's bow: dark yew bound in iron, an arrow nocked, red notches along the limbs.
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8 - 0.5;
        pts.push(V(-Math.cos(t * Math.PI) * 0.16 + (Math.abs(t) > 0.4 ? -0.03 : 0), t * 1.0, 0));
      }
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 16, 0.022, 5), DWOOD, it);
      limb(it, pts[0]!, pts[8]!, 0.004, 0.004, std('#d8d0bd'), 3);
      M(new THREE.CylinderGeometry(0.03, 0.03, 0.12, 6), LEATHER_BELT, it).position.set(-0.16, 0, 0);
      limb(it, V(0, 0, 0), V(-0.4, 0, 0), 0.007, 0.007, WOOD, 4).position.x = 0.02;
      const head = M(new THREE.ConeGeometry(0.025, 0.07, 4), hot, it);
      head.rotation.z = Math.PI / 2;
      head.position.set(-0.43, 0, 0);
      for (const y of [-0.3, -0.2, 0.2, 0.3]) M(new THREE.BoxGeometry(0.02, 0.02, 0.03), rune, it).position.set(-0.13 + Math.abs(y) * 0.12, y, 0.015);
      it.rotation.y = Math.PI / 2;
      break;
    }
    case 'wallAxe': {
      // The wall's axe: a long haft and a blade of wall stone set in iron, a red rune in the stone.
      limb(it, V(0, -0.55, 0), V(0, 0.35, 0), 0.025, 0.025, DWOOD, 6);
      const head = G(it, 0, 0.25, 0);
      const s = new THREE.Shape();
      s.moveTo(0, 0.12);
      s.quadraticCurveTo(0.22, 0.2, 0.3, 0.04);
      s.quadraticCurveTo(0.33, -0.1, 0.3, -0.24);
      s.quadraticCurveTo(0.2, -0.1, 0, -0.1);
      s.closePath();
      const bl = M(new THREE.ExtrudeGeometry(s, { depth: 0.04, bevelEnabled: true, bevelThickness: 0.01, bevelSize: 0.01, bevelSegments: 1 }), stone('#6e6a66'), head);
      bl.position.z = -0.02;
      M(new THREE.BoxGeometry(0.08, 0.24, 0.07), DSTEEL, head).position.set(0.02, 0.01, 0);
      runes(head, V(0.14, 0.06, 0.035), V(0.2, -0.1, 0.035), 3, 0.02, 0.03);
      gem(head, V(0.22, -0.02, 0.04), 0.03);
      break;
    }
    case 'gasparHammer': {
      // Ser Gaspar's hammer: blued steel, a ribbon of his blue, a red seal on the head.
      limb(it, V(0, -0.5, 0), V(0, 0.3, 0), 0.022, 0.022, DWOOD, 6);
      const hd = M(new THREE.BoxGeometry(0.3, 0.14, 0.14), metal('#4a5f84', { roughness: 0.3 }), it);
      hd.position.y = 0.34;
      M(new THREE.ConeGeometry(0.05, 0.12, 4), metal('#4a5f84', { roughness: 0.3 }), it).position.set(-0.2, 0.34, 0);
      (it.children[it.children.length - 1] as THREE.Mesh).rotation.z = Math.PI / 2;
      gem(it, V(0, 0.34, 0.075), 0.04);
      runes(it, V(0.08, 0.34, 0.072), V(0.13, 0.34, 0.072), 2, 0.015, 0.06);
      new Ribbon(it, [V(0, 0.22, 0), V(0.05, 0.12, 0.04), V(0.03, 0.0, 0.06), V(0.08, -0.12, 0.05)], 0.018, cloth('#2a3e6a'), rng, 0.01);
      break;
    }
    case 'oldRootStaff': {
      // The old-root staff: a twisted root, a seed of red fire caught in its knot.
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        pts.push(V(Math.sin(t * 9) * 0.03, -0.55 + t * 1.0, Math.cos(t * 7) * 0.03));
      }
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 24, 0.028, 6), std('#4a3424', { roughness: 0.95 }), it);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([V(0, 0.38, 0), V(Math.cos(a) * 0.1, 0.48, Math.sin(a) * 0.1), V(Math.cos(a) * 0.05, 0.6, Math.sin(a) * 0.05)]), 8, 0.012, 4), std('#4a3424'), it);
      }
      gem(it, V(0, 0.5, 0), 0.05);
      for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.03, 5, 4), std('#3f6a32', { roughness: 1 }), it).position.set(Math.sin(i) * 0.04, -0.2 + i * 0.12, Math.cos(i) * 0.04);
      break;
    }
    /* armour */
    case 'lastWatch':
    case 'oathPlate': {
      // A cuirass on its stand: the last watch's, dark and dented; the oath's, bright, with its red seal.
      const bright = o.id === 'oathPlate';
      const pl = metal(bright ? '#c4cad2' : '#5f656d', { roughness: bright ? 0.2 : 0.45, flatShading: true });
      torso(pl);
      for (const k of [-1, 1]) {
        const pa = pauldron(it, k, pl, bright ? 1.5 : 1.25, bright ? 2 : 1, bright ? metal('#7a2328') : DSTEEL);
        pa.position.set(k * 0.23, 0.2, 0);
      }
      // A ridge down the middle, and the red rune across the chest.
      M(new THREE.BoxGeometry(0.02, 0.36, 0.03), pl, it).position.set(0, 0.02, 0.165);
      runes(it, V(-0.1, 0.12, 0.16), V(0.1, 0.12, 0.16), 5, 0.022, 0.05);
      gem(it, V(0, -0.06, 0.16), 0.05);
      if (bright) new Ribbon(it, [V(0, -0.08, 0.17), V(0.03, -0.2, 0.19), V(-0.01, -0.34, 0.18)], 0.02, cloth('#7a2328'), rng, 0.01);
      else for (const [x, y] of [[0.1, -0.05], [-0.12, 0.0], [0.06, -0.18]] as const) M(new THREE.SphereGeometry(0.025, 5, 4), DSTEEL, it).position.set(x, y, 0.15);
      height = 0.85;
      break;
    }
    case 'anvilHarness': {
      // The Anvil clan's harness: a leather jerkin, straps crossing an iron anvil plate, a gear, a red core.
      torso(leather('#4a3020'));
      for (const k of [-1, 1]) {
        const strap = M(new THREE.BoxGeometry(0.055, 0.6, 0.02), LEATHER_BELT, it);
        strap.position.set(0, 0.0, 0.165);
        strap.rotation.z = k * 0.55;
        M(new THREE.BoxGeometry(0.06, 0.06, 0.03), DSTEEL, it).position.set(k * 0.12, 0.17, 0.17);
      }
      const anv = G(it, 0, 0.0, 0.18);
      M(new THREE.BoxGeometry(0.22, 0.07, 0.05), DSTEEL, anv);
      M(new THREE.BoxGeometry(0.11, 0.08, 0.05), DSTEEL, anv).position.y = -0.07;
      const horn = M(new THREE.ConeGeometry(0.035, 0.1, 4), DSTEEL, anv);
      horn.position.set(0.16, 0, 0);
      horn.rotation.z = -Math.PI / 2;
      gem(anv, V(0, -0.07, 0.03), 0.04);
      const gr = M(new THREE.TorusGeometry(0.07, 0.018, 5, 12), metal('#8c5a2b'), it);
      gr.position.set(-0.14, 0.2, 0.13);
      height = 0.85;
      break;
    }
    case 'frostMantle': {
      // The frost mantle: a cold blue cloak with a white fur collar, rimed with ice, clasped with a red stone.
      const m = M(new THREE.CylinderGeometry(0.15, 0.34, 0.72, 16, 1, true, -Math.PI * 0.85, Math.PI * 1.7), std('#5e7a94', { side: THREE.DoubleSide, roughness: 0.9, bumpScale: 0.03 }), it);
      m.position.y = -0.06;
      m.rotation.y = Math.PI;
      const collar = M(new THREE.TorusGeometry(0.16, 0.05, 6, 16), std('#e6eef4', { roughness: 1, bumpScale: 0.05 }), it);
      collar.position.y = 0.3;
      collar.rotation.x = Math.PI / 2;
      const ice = emis('#eaf8ff', '#7fc8ff', 0.7, { flatShading: true, transparent: true, opacity: 0.9 });
      for (let i = 0; i < 11; i++) {
        const a = -2.4 + (i / 10) * 4.8;
        const s = M(new THREE.OctahedronGeometry(0.035), ice, it);
        s.position.set(Math.sin(a) * 0.33, -0.4 + (i % 2) * 0.06, Math.cos(a) * 0.33);
        s.scale.y = 2.2;
      }
      gem(it, V(0, 0.27, 0.19), 0.05);
      runes(it, V(-0.24, -0.36, 0.22), V(0.24, -0.36, 0.22), 5, 0.018, 0.03);
      height = 0.85;
      break;
    }
    case 'dwarfMail': {
      // Dwarven mail: a short coat of tight iron rings, a red rune on the chest.
      const mail = metal('#8a9098', { roughness: 0.55 });
      torso(mail, 1.06);
      for (let i = 0; i < 9; i++) {
        const y = -0.26 + i * 0.065;
        const r = M(new THREE.TorusGeometry(torsoR(y) * 1.07, 0.009, 3, 22), DSTEEL, it);
        r.position.y = y;
        r.rotation.x = Math.PI / 2;
        r.scale.y = 0.68;
      }
      runes(it, V(-0.06, 0.12, 0.17), V(0.06, 0.12, 0.17), 3, 0.022, 0.05);
      gem(it, V(0, -0.02, 0.17), 0.045);
      height = 0.85;
      break;
    }
    case 'wandererCloak': {
      // The wanderer's cloak: deep green, hooded, a red clasp.
      const cm = std('#2e4030', { side: THREE.DoubleSide, roughness: 1, bumpScale: 0.03 });
      M(new THREE.CylinderGeometry(0.14, 0.34, 0.74, 14, 1, true, -Math.PI * 0.9, Math.PI * 1.8), cm, it).rotation.y = Math.PI;
      const hood = M(new THREE.SphereGeometry(0.16, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), cm, it);
      hood.position.set(0, 0.36, -0.04);
      hood.rotation.x = -0.4;
      gem(it, V(0, 0.33, 0.13), 0.045);
      runes(it, V(-0.2, -0.32, 0.25), V(0.2, -0.32, 0.25), 5, 0.018, 0.03);
      height = 0.9;
      break;
    }
    case 'boarHide': {
      // The stone boar's hide: a heavy grey pelt over the shoulders, the boar's head on top, tusks and red paint.
      const fur = std('#55504a', { roughness: 1, bumpScale: 0.05, side: THREE.DoubleSide });
      const pelt = M(new THREE.CylinderGeometry(0.16, 0.36, 0.55, 14, 1, true), fur, it);
      pelt.position.y = -0.1;
      pelt.scale.z = 0.8;
      const head = G(it, 0, 0.24, 0.06);
      const skull = M(new THREE.SphereGeometry(0.14, 10, 8), fur, head);
      skull.scale.set(1, 0.85, 1.1);
      const snout = M(new THREE.CylinderGeometry(0.06, 0.08, 0.14, 8), fur, head);
      snout.rotation.x = Math.PI / 2;
      snout.position.set(0, -0.03, 0.15);
      for (const k of [-1, 1]) {
        const tusk = M(new THREE.ConeGeometry(0.022, 0.15, 6), std('#d8ccb0'), head);
        tusk.position.set(k * 0.06, -0.02, 0.22);
        tusk.rotation.set(-1.0, 0, -k * 0.45);
        const ear = M(new THREE.ConeGeometry(0.04, 0.09, 4), fur, head);
        ear.position.set(k * 0.1, 0.1, -0.02);
        ear.rotation.z = -k * 0.5;
        M(new THREE.SphereGeometry(0.014, 5, 4), hot, head).position.set(k * 0.06, 0.04, 0.12);
      }
      for (const k of [-1, 0, 1]) M(new THREE.BoxGeometry(0.03, 0.18, 0.012), rune, it).position.set(k * 0.09, -0.12, 0.29);
      height = 0.8;
      break;
    }
    case 'dwarfBark': {
      // Dwarf bark: plates of bark over a body, bound in iron, red sap glowing in the seams.
      const bark = std('#4a3424', { roughness: 1, flatShading: true, bumpScale: 0.05 });
      torso(bark, 1.0);
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        const pl = M(new THREE.BoxGeometry(0.11, 0.46, 0.04), bark, it);
        pl.position.set(Math.sin(a) * 0.205, -0.02, Math.cos(a) * 0.14);
        pl.rotation.set(0, a, 0.05 * (i % 2 ? 1 : -1));
      }
      for (const y of [-0.14, 0.12]) M(new THREE.TorusGeometry(torsoR(y) * 1.12, 0.014, 4, 18), DSTEEL, it).position.y = y;
      for (const m of it.children.slice(-2)) {
        m.rotation.x = Math.PI / 2;
        m.scale.y = 0.7;
      }
      for (let i = 0; i < 3; i++) limb(it, V(-0.06 + i * 0.06, -0.24, 0.16), V(-0.04 + i * 0.05, 0.22, 0.17), 0.009, 0.009, hot, 3);
      height = 0.8;
      break;
    }
    /* amulets */
    case 'posternMedal':
    case 'tailFang':
    case 'stormEye':
    case 'maelisSeed':
    case 'maelisKit':
    case 'healerRosary':
    case 'mossWhistle':
    case 'vigilCandle': {
      // A pendant on a cord, and what hangs from it.
      const cord = G(it, 0, 0.06, 0);
      for (let i = 0; i < 9; i++) {
        const a = (i / 8) * Math.PI * 1.2 - Math.PI * 0.6;
        const l = M(new THREE.SphereGeometry(0.011, 4, 3), def.cls === 'barbarian' ? std('#3a2a20') : DSTEEL, cord);
        l.position.set(Math.sin(a) * 0.1, (1 - Math.cos(a)) * 0.12, 0);
      }
      const pd = G(it, 0, -0.02, 0);
      if (o.id === 'posternMedal') {
        // The postern's medal: a disc with the little door on it, a red ribbon.
        const d = M(new THREE.CylinderGeometry(0.13, 0.13, 0.03, 16), metal('#9aa0a8', { roughness: 0.3 }), pd);
        d.rotation.x = Math.PI / 2;
        const door = new THREE.Shape();
        door.moveTo(-0.04, -0.06);
        door.lineTo(0.04, -0.06);
        door.lineTo(0.04, 0.02);
        door.absarc(0, 0.02, 0.04, 0, Math.PI, false);
        door.closePath();
        M(new THREE.ExtrudeGeometry(door, { depth: 0.01, bevelEnabled: false }), rune, pd).position.z = 0.016;
        ring(pd, 0.13, 0.012, DSTEEL, 0, 4, 20).rotation.x = 0;
        new Ribbon(it, [V(-0.05, 0.12, 0.02), V(0, 0.06, 0.03), V(0.05, 0.12, 0.02)], 0.02, cloth('#8a2a2a'), rng, 0.005);
      } else if (o.id === 'tailFang') {
        // The tail's fang: a great curved tooth bound in leather and iron, two lesser fangs and bone beads
        // beside it, red runes cut along its curve.
        const bone = std('#d8ccb0', { roughness: 0.6 });
        const pts = [V(0, 0.04, 0), V(0.05, -0.12, 0), V(0.04, -0.3, 0), V(-0.05, -0.44, 0)];
        for (let i = 0; i < 3; i++) limb(pd, pts[i]!, pts[i + 1]!, 0.06 - i * 0.018, 0.044 - i * 0.017, bone, 7);
        M(new THREE.CylinderGeometry(0.062, 0.062, 0.05, 8), leather('#3a2a1e'), pd).position.y = 0.0;
        ring(pd, 0.064, 0.01, DSTEEL, 0.03, 4, 12);
        ring(pd, 0.064, 0.01, DSTEEL, -0.03, 4, 12);
        for (const k of [-1, 1]) {
          const small = [V(k * 0.1, -0.02, 0), V(k * 0.12, -0.1, 0), V(k * 0.09, -0.17, 0)];
          for (let i = 0; i < 2; i++) limb(pd, small[i]!, small[i + 1]!, 0.022 - i * 0.008, 0.014 - i * 0.008, bone, 5);
          for (let i = 0; i < 2; i++) M(new THREE.SphereGeometry(0.018, 6, 4), bone, pd).position.set(k * (0.05 + i * 0.03), 0.06 - i * 0.02, 0);
        }
        runes(pd, V(0.05, -0.08, 0.05), V(0.03, -0.3, 0.03), 4, 0.016, 0.022);
      } else if (o.id === 'stormEye') {
        // The storm's eye: a dark iron setting round an eye of red glass, sparks circling it.
        const d = M(new THREE.TorusGeometry(0.1, 0.03, 6, 16), DSTEEL, pd);
        d.position.y = -0.06;
        const eye = M(new THREE.SphereGeometry(0.075, 10, 8), emis('#ffc8bc', '#ff4f3a', 1.3), pd);
        eye.position.set(0, -0.06, 0);
        eye.scale.z = 0.6;
        M(new THREE.SphereGeometry(0.03, 8, 6), std('#0c0a0c'), pd).position.set(0, -0.06, 0.04);
        glowSprite(pd, '#ff4f3a', 0.5, V(0, -0.06, 0.05), 0.6);
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          glowSprite(pd, '#bcd4ff', 0.09, V(Math.cos(a) * 0.17, -0.06 + Math.sin(a) * 0.17, 0), 0.9);
        }
      } else if (o.id === 'maelisSeed') {
        // Maelis's seed: a thorned seed pod split open on a seed of red light, in a stoppered glass vial
        // bound with a sprig of herbs.
        const vial = M(new THREE.CylinderGeometry(0.075, 0.075, 0.26, 10, 1, true), std('#bfe0d0', { roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.35, side: THREE.DoubleSide }), pd);
        vial.position.y = -0.12;
        M(new THREE.SphereGeometry(0.075, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), std('#bfe0d0', { roughness: 0.05, transparent: true, opacity: 0.35 }), pd).position.y = -0.25;
        M(new THREE.CylinderGeometry(0.05, 0.045, 0.05, 8), DWOOD, pd).position.y = 0.03;
        const pod = std('#3e5a2e', { roughness: 0.8, flatShading: true });
        for (let i = 0; i < 4; i++) {
          const half = M(new THREE.SphereGeometry(0.045, 8, 6, 0, Math.PI), pod, pd);
          half.position.set(0, -0.14, 0);
          half.rotation.set(0.4, (i * Math.PI) / 2, 0.5);
          half.scale.set(0.6, 1.4, 1);
          M(new THREE.ConeGeometry(0.008, 0.04, 3), pod, pd).position.set(Math.cos(i * 1.57) * 0.05, -0.13 + (i % 2) * 0.04, Math.sin(i * 1.57) * 0.05);
        }
        gem(pd, V(0, -0.14, 0), 0.03);
        for (let i = 0; i < 3; i++) limb(pd, V(0.06, 0.0, 0.02), V(0.1 + i * 0.03, 0.1 - i * 0.02, 0.03), 0.006, 0.004, std('#4a6a34'), 3);
      } else if (o.id === 'maelisKit') {
        // Maelis's kit: a leather pouch and three vials, one of them burning red.
        const pouch = M(new THREE.BoxGeometry(0.22, 0.16, 0.08), leather('#4a3020'), pd);
        pouch.position.y = -0.12;
        for (let i = 0; i < 3; i++) {
          const v = M(new THREE.CylinderGeometry(0.022, 0.022, 0.1, 8), i === 1 ? emis('#ffc8bc', '#ff4f3a', 1.4) : std('#7fb8a8', { transparent: true, opacity: 0.8 }), pd);
          v.position.set(-0.06 + i * 0.06, 0.0, 0);
          M(new THREE.CylinderGeometry(0.012, 0.012, 0.03, 6), WOOD, pd).position.set(-0.06 + i * 0.06, 0.06, 0);
        }
        glowSprite(pd, '#ff4f3a', 0.35, V(0, 0, 0.03), 0.6);
      } else if (o.id === 'healerRosary') {
        // The healer's rosary: a loop of dark beads, one of them red, and a small sun of iron.
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          M(new THREE.SphereGeometry(i === 0 ? 0.028 : 0.018, 6, 5), i === 0 ? hot : std('#3a2a24'), pd).position.set(Math.sin(a) * 0.12, -0.14 + Math.cos(a) * 0.12, 0);
        }
        const sun = G(pd, 0, -0.32, 0);
        M(new THREE.CylinderGeometry(0.04, 0.04, 0.015, 10), DSTEEL, sun).rotation.x = Math.PI / 2;
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          const ray = M(new THREE.BoxGeometry(0.012, 0.04, 0.01), DSTEEL, sun);
          ray.position.set(Math.cos(a) * 0.06, Math.sin(a) * 0.06, 0);
          ray.rotation.z = a - Math.PI / 2;
        }
        gem(sun, V(0, 0, 0.012), 0.02);
      } else if (o.id === 'mossWhistle') {
        // Musgoviejo's whistle: carved wood with moss on it, a red cord.
        const w = M(new THREE.CylinderGeometry(0.035, 0.03, 0.26, 8), std('#6a4a30', { roughness: 0.9 }), pd);
        w.rotation.z = Math.PI / 2;
        w.position.y = -0.08;
        M(new THREE.BoxGeometry(0.04, 0.02, 0.05), std('#1a1412'), pd).position.set(0.06, -0.05, 0);
        for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.02, 5, 4), std('#4f6b34', { roughness: 1 }), pd).position.set(-0.08 + i * 0.04, -0.11, 0.02);
        runes(pd, V(-0.06, -0.08, 0.036), V(0.02, -0.08, 0.036), 3, 0.012, 0.025);
      } else if (o.id === 'vigilCandle') {
        // The vigil candle: a stub of candle in an iron holder, burning red.
        M(new THREE.CylinderGeometry(0.06, 0.08, 0.04, 10), DSTEEL, pd).position.y = -0.28;
        M(new THREE.CylinderGeometry(0.035, 0.035, 0.18, 10), std('#d8d4cc', { roughness: 0.8 }), pd).position.y = -0.17;
        flames.push(flame(pd, V(0, -0.07, 0), 0.4, rng, '#ff4f3a'));
        glowSprite(pd, '#ff6a50', 0.45, V(0, -0.02, 0), 0.6);
      }
      height = 0.95;
    }
  }
  if (def.slot === 'weapon' && o.id !== 'watchBow') it.rotation.z = -0.7;
  if (def.slot === 'weapon') it.scale.setScalar(1.15);
  if (def.slot === 'amulet') it.scale.setScalar(1.8);
  // Every unique piece: a faint red aura round it, the same for all.
  glowSprite(g, '#ff4f3a', 1.1, V(0, 0.5, 0), 0.16);
  const tick = (n: number): void => {
    it.rotation.y = (o.id === 'watchBow' ? Math.PI / 2 : 0) + Math.sin(n * 0.2) * 0.6;
    it.position.y = (def.slot === 'amulet' ? 0.55 : 0.5) + (n % 12 < 6 ? 0.015 : 0);
    for (const f of flames) f.flick();
  };
  tick(0);
  return done(c, height, tick);
};

